import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, ButtonGroup, Col, Dropdown, Form, Modal, Nav, Row, Spinner, Tab, Table } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { SETTINGS_KEYS } from '../../config/environment'
import { correspondentService } from '../../services/correspondent.service'
import { customFieldService } from '../../services/custom-field.service'
import { documentListViewService } from '../../services/document-list-view.service'
import { documentTypeService } from '../../services/document-type.service'
import { documentWorkflowHistoryService, type WorkflowHistoryEntry } from '../../services/document-workflow-history.service'
import { documentNotesService } from '../../services/document-notes.service'
import { documentService } from '../../services/document.service'
import { openDocumentsService } from '../../services/open-documents.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { settingsService } from '../../services/settings.service'
import { storagePathService } from '../../services/storage-path.service'
import { tagService } from '../../services/tag.service'
import { toastService } from '../../services/toast.service'
import { workflowStatusService } from '../../services/workflow-status.service'
import {
  Correspondent,
  CustomField,
  CustomFieldInstance,
  Document,
  DocumentNote,
  DocumentType,
  FilterRule,
  StoragePath,
  Tag,
  WorkflowStatus,
} from '../../types'
import { computeHiddenFieldIds } from '../../utils/custom-field-query'
import { formatDate, formatDateTime } from '../../utils/dateFormatter'
import { ownerDisplayFromDocument } from '../../utils/documentOwnerDisplay'
import { queryParamsFromFilterRules } from '../../utils/queryParams'
import { ConfirmDialog } from '../common/ConfirmDialog'
import CustomFieldsDropdown from '../common/CustomFieldsDropdown'
import { DocumentationDialog } from '../common/DocumentationDialog'
import { DocumentLinkInput } from '../common/DocumentLinkInput'
import { MonetaryInput } from '../common/MonetaryInput'
import DocuSignSendDialog from '../common/DocuSignSendDialog'
import EmailDocumentDialog from '../common/EmailDocumentDialog'
import { InputTags } from '../common/InputTags'
import PageHeader from '../common/PageHeader'
import PDFEditor from '../common/PDFEditor'
import { PermissionsForm, PermissionsFormValue } from '../common/PermissionsForm'
import ShareLinksDialog from '../common/ShareLinksDialog'
import { CorrespondentEditDialog } from '../Correspondents/CorrespondentEditDialog'
import DocumentHistory from '../DocumentHistory/DocumentHistory'
import DocumentNotes from '../DocumentNotes/DocumentNotes'
import { DocumentTypeEditDialog } from '../DocumentTypes/DocumentTypeEditDialog'
import DocumentVersions from '../DocumentVersions/DocumentVersions'
import { StoragePathEditDialog } from '../StoragePaths/StoragePathEditDialog'
import { TagEditDialog } from '../Tags/TagEditDialog'
import { WorkflowStatusEditDialog } from '../WorkflowStatuses/WorkflowStatusEditDialog'
import './DocumentDetail.scss'
import { DocumentDetailWorkflow } from './DocumentDetailWorkflow'
import DocumentESignAudit from './DocumentESignAudit'
import { RelatedItemsSection } from './RelatedItemsSection'
import { DocumentWorkflowStartChoiceModal } from './DocumentWorkflowStartChoiceModal'

// Filter rule types (same as React)
const FILTER_CORRESPONDENT = 3
const FILTER_DOCUMENT_TYPE = 4
const FILTER_STORAGE_PATH = 25
const FILTER_HAS_TAGS_ALL = 6
const FILTER_FULLTEXT_MORELIKE = 21

enum DocumentDetailNavIDs {
  Details = 'details',
  Content = 'content',
  Metadata = 'metadata',
  Notes = 'notes',
  Versions = 'versions',
  WorkflowHistory = 'workflow-history',
  History = 'history',
  ESignAudit = 'esign-audit',
  Permissions = 'permissions',
}

type WorkflowAvailableActionsApi = Awaited<ReturnType<typeof documentService.getWorkflowAvailableActions>>

type WorkflowRequiredAttributeDetail = {
  kind: 'custom_field' | 'system_field'
  field_id?: number
  system_field?: string
  name: string
}

type WorkflowRequiredFieldErrors = {
  customFields: Set<number>
  systemFields: Set<string>
}

/** Single mapping from API → sidebar workflow state (avoid drift between call sites). */
function workflowAvailableActionsFromApi(actionsData: WorkflowAvailableActionsApi) {
  return {
    in_workflow: actionsData.in_workflow,
    workflow_definition: actionsData.workflow_definition,
    status_id: actionsData.status_id ?? undefined,
    current_step: actionsData.current_step ?? null,
    available_actions: actionsData.available_actions,
    available_definitions: actionsData.available_definitions,
    user_can_claim: 'user_can_claim' in actionsData ? actionsData.user_can_claim : false,
    has_multiple_assignees: 'has_multiple_assignees' in actionsData ? actionsData.has_multiple_assignees : false,
    user_can_perform_actions: 'user_can_perform_actions' in actionsData ? actionsData.user_can_perform_actions : false,
    steps: actionsData.steps,
    current_step_assignee_usernames: actionsData.current_step_assignee_usernames,
    current_step_assignee_group_names: actionsData.current_step_assignee_group_names,
  }
}

const DocumentDetail: React.FC = () => {
  const { id, section } = useParams<{ id: string; section?: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const documentId = parseInt(id || '0', 10)
  const queryClient = useQueryClient()

  const [document, setDocument] = useState<Document | null>(null)
  const [metadata, setMetadata] = useState<any>(null)
  const [suggestions, setSuggestions] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  const [correspondents, setCorrespondents] = useState<Correspondent[]>([])
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([])
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>([])
  const [storagePaths, setStoragePaths] = useState<StoragePath[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [allCustomFields, setAllCustomFields] = useState<CustomField[]>([]) // All custom fields for rendering
  const [permissionsForm, setPermissionsForm] = useState<PermissionsFormValue>({
    owner: null,
    set_permissions: {
      view: { users: [], groups: [] },
      change: { users: [], groups: [] },
    },
  })

  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [confirmAction, setConfirmAction] = useState<((permanentDelete?: boolean) => void) | null>(null)
  const [isDeleteSubmitting, setIsDeleteSubmitting] = useState(false)
  const [isRefreshTemplateSubmitting, setIsRefreshTemplateSubmitting] = useState(false)
  const [confirmDialogConfig, setConfirmDialogConfig] = useState<{
    title: string
    message: string
    messageBold?: string
    confirmText?: string
    variant?: 'danger' | 'warning' | 'primary'
    showPermanentDeleteOption?: boolean
    permanentDeleteLabel?: string
    permanentDeleteDescription?: string
  }>({
    title: t('confirm_delete'),
    message: t('confirm_move_to_trash', { count: 1 }),
    confirmText: t('delete'),
    variant: 'danger',
  })
  const [showCloseDocumentDialog, setShowCloseDocumentDialog] = useState(false)
  const [showVersionWarning, setShowVersionWarning] = useState(false)
  const [showDocumentTypeChangeDialog, setShowDocumentTypeChangeDialog] = useState(false)
  const [showDocuSignSendDialog, setShowDocuSignSendDialog] = useState(false)
  const [showWorkflowStepperModal, setShowWorkflowStepperModal] = useState(false)
  const [showStartWorkflowChoiceModal, setShowStartWorkflowChoiceModal] = useState(false)
  const [showUnsavedWorkflowDialog, setShowUnsavedWorkflowDialog] = useState(false)
  const [workflowContinuing, setWorkflowContinuing] = useState(false)
  const [pendingWorkflowContinuation, setPendingWorkflowContinuation] = useState<
    | { type: 'action'; actionId: number; comment?: string }
    | { type: 'start'; definitionId: number }
    | null
  >(null)
  const [pendingDocumentTypeChange, setPendingDocumentTypeChange] = useState<any>(null)
  const [documentTypeChangeCustomFieldsMode, setDocumentTypeChangeCustomFieldsMode] = useState<'keep' | 'replace'>('keep')
  const [showShareLinksDialog, setShowShareLinksDialog] = useState(false)
  const [showEmailDialog, setShowEmailDialog] = useState(false)
  const [showPdfEditor, setShowPdfEditor] = useState(false)
  const [showDocsDialog, setShowDocsDialog] = useState(false)
  const [showTabsDocsDialog, setShowTabsDocsDialog] = useState(false)
  const [showNewVersionDialog, setShowNewVersionDialog] = useState(false)
  const [newVersionFile, setNewVersionFile] = useState<File | null>(null)
  const [newVersionFileName, setNewVersionFileName] = useState<string>('')
  const newVersionFileInputRef = useRef<HTMLInputElement>(null)
  const [newVersionComment, setNewVersionComment] = useState('')
  const [creatingVersion, setCreatingVersion] = useState(false)
  const [versionsRefreshKey, setVersionsRefreshKey] = useState(0)
  const [editingCreatedDate, setEditingCreatedDate] = useState(false)

  // Track document ID for which version warning was already shown
  const lastDocumentIdWarning = useRef<number | null>(null)
  const accessOpenedLoggedForId = useRef<number | null>(null)

  // Edit dialog states
  const [showCorrespondentDialog, setShowCorrespondentDialog] = useState(false)
  const [showDocumentTypeDialog, setShowDocumentTypeDialog] = useState(false)
  const [showWorkflowStatusDialog, setShowWorkflowStatusDialog] = useState(false)
  const [showStoragePathDialog, setShowStoragePathDialog] = useState(false)
  const [showTagDialog, setShowTagDialog] = useState(false)

  /** Dostupne akcije iz definisanog workflow-a (start/pokretanje workflow-a) */
  const [workflowAvailableActions, setWorkflowAvailableActions] = useState<{
    in_workflow: boolean
    workflow_definition: { id: number; name: string } | null
    status_id?: number | null
    current_step: { id: number; status_id: number; position: number } | null
    available_actions: Array<{ id: number; name: string; end_workflow: boolean; color?: string; text_color?: string }>
    available_definitions: Array<{ id: number; name: string }>
    user_can_claim: boolean
    has_multiple_assignees?: boolean
    user_can_perform_actions?: boolean
    steps?: Array<{ id: number; position: number; status_id: number; name?: string }>
    current_step_assignee_usernames?: string[]
    current_step_assignee_group_names?: string[]
  } | null>(null)
  const [workflowRequiredFieldErrors, setWorkflowRequiredFieldErrors] = useState<WorkflowRequiredFieldErrors>({
    customFields: new Set<number>(),
    systemFields: new Set<string>(),
  })

  /** Workflow history (audit log – stavka 24 + claim/unclaim 16) */
  const [workflowHistory, setWorkflowHistory] = useState<WorkflowHistoryEntry[]>([])
  const [claimSubmitting, setClaimSubmitting] = useState(false)
  const [workflowHistoryLoading, setWorkflowHistoryLoading] = useState(false)

  /** Podtab unutar History: all | workflow | esign */
  const [historySubTab, setHistorySubTab] = useState<'all' | 'workflow' | 'esign'>('all')
  const docusignEnabled = settingsService.getBoolean('docusign_enabled', false)
  // Ako je eSign podtab izabran a DocuSign isključen, prikaži All
  const effectiveHistorySubTab = historySubTab === 'esign' && !docusignEnabled ? 'all' : historySubTab

  const [formData, setFormData] = useState<Partial<Document>>({})

  // Track document list state for navigation buttons
  const [documentsListState, setDocumentsListState] = useState({
    documents: documentListViewService.documents,
    isReloading: documentListViewService.isReloading,
    updateCount: documentListViewService.updateCount,
  })

  // Determine active tab from URL section parameter
  const getActiveTabFromSection = (sectionParam?: string): string => {
    if (!sectionParam) {
      return DocumentDetailNavIDs.Details
    }
    // Map section string to nav ID (case-insensitive)
    const sectionLower = sectionParam.toLowerCase()
    const navKeys = Object.keys(DocumentDetailNavIDs) as Array<keyof typeof DocumentDetailNavIDs>
    const matchingKey = navKeys.find((key) => DocumentDetailNavIDs[key].toLowerCase() === sectionLower)
    return matchingKey ? DocumentDetailNavIDs[matchingKey] : DocumentDetailNavIDs.Details
  }

  const activeTab = getActiveTabFromSection(section)

  // Preview state
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null)
  const [previewText, setPreviewText] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(true)
  const [pdfZoom, setPdfZoom] = useState(1.0)
  const [previewMimeType, setPreviewMimeType] = useState<string | null>(null)

  const loadPreview = useCallback(async () => {
    try {
      setPreviewLoading(true)
      const mimeType = document?.mime_type || ''
      const isTextFile = mimeType.startsWith('text/') || mimeType === 'application/csv'

      if (isTextFile) {
        // For text files, load as text
        const blob = await documentService.downloadDocument(documentId, false)
        const text = await blob.text()
        setPreviewText(text)
        setPreviewObjectUrl(null)
        setPreviewMimeType(mimeType || blob.type || 'text/plain')
      } else {
        // Download as blob for images & PDFs
        const blob = await documentService.downloadDocument(documentId, mimeType.startsWith('image/') || false)
        const url = URL.createObjectURL(blob)
        setPreviewObjectUrl(url)
        setPreviewText(null)
        setPreviewMimeType(blob.type || mimeType || null)
      }
      setPreviewLoading(false)
    } catch (error) {
      setPreviewLoading(false)
    }
  }, [documentId, document?.mime_type])

  const loadDocument = useCallback(
    async (useServerVersion?: boolean) => {
      try {
        setLoading(true)
        const doc = await documentService.getDocument(documentId)

        // API može vratiti aktivnu verziju ako je URL zastareo (npr. lista
        // povezanih dokumenata učitana pre promene verzije).
        if (doc.id !== documentId) {
          const targetSection = section || 'details'
          navigate(`/documents/${doc.id}/${targetSection}`, { replace: true })
          return
        }

        const openDocument = openDocumentsService.getOpenDocument(documentId)
        let useDoc = doc

        if (useServerVersion) {
          openDocumentsService.updateDocument(doc)
          setIsDirty(false)
        } else {
          useDoc = openDocument || doc
          if (openDocument && lastDocumentIdWarning.current !== documentId) {
            if (new Date(doc.modified) > new Date(openDocument.modified)) {
              setShowVersionWarning(true)
              lastDocumentIdWarning.current = documentId
              setIsDirty(true)
            }
          } else if (!openDocument) {
            openDocumentsService.openDocument(doc)
          }
        }

        setDocument(useDoc)
        setFormData(useDoc)

        // Map document permissions to permissions_form format
        // Owner can be User object or number - extract ID
        const ownerId = useDoc.owner ? (typeof useDoc.owner === 'number' ? useDoc.owner : useDoc.owner.id) : null
        // Permissions may not be in Document type, but API returns it
        const docPermissions = (useDoc as any).permissions || (useDoc as any).set_permissions
        setPermissionsForm({
          owner: ownerId,
          set_permissions: docPermissions || {
            view: { users: [], groups: [] },
            change: { users: [], groups: [] },
          },
        })

        // If document already has unsaved changes (from previous session), mark as dirty
        if (openDocument && openDocumentsService.isDirty(openDocument)) {
          setIsDirty(true)
        }

        setLoading(false)
      } catch (error: unknown) {
        setLoading(false)
        const ax = error as { response?: { status?: number; data?: { code?: string } } }
        const status = ax?.response?.status
        const code = ax?.response?.data?.code
        if (status === 404 && code === 'document_wrong_organization') {
          const openDoc = openDocumentsService.getOpenDocument(documentId)
          if (openDoc) {
            openDocumentsService.closeDocument(openDoc)
          }
          navigate('/documents', { replace: true })
          return
        }
        toastService.showError('Error loading document', error)
        navigate('/documents', { replace: true })
      }
    },
    [documentId, navigate, section, t]
  )

  /** After workflow mutations: reload document + workflow actions + history in one round-trip so UI never shows mixed ages. */
  const refreshDocumentWorkflowBundle = useCallback(async (docId: number) => {
    const [freshDoc, actionsData, historyData] = await Promise.all([
      documentService.getDocument(docId),
      documentService.getWorkflowAvailableActions(docId),
      documentWorkflowHistoryService.getWorkflowHistory(docId),
    ])
    setWorkflowAvailableActions(workflowAvailableActionsFromApi(actionsData))
    setWorkflowHistory(historyData)
    openDocumentsService.updateDocument(freshDoc)
    setDocument(freshDoc)
    setFormData(freshDoc)
    const ownerId = freshDoc.owner ? (typeof freshDoc.owner === 'number' ? freshDoc.owner : freshDoc.owner.id) : null
    const docPermissions = (freshDoc as any).permissions || (freshDoc as any).set_permissions
    setPermissionsForm({
      owner: ownerId,
      set_permissions: docPermissions || {
        view: { users: [], groups: [] },
        change: { users: [], groups: [] },
      },
    })
  }, [])

  /** When document was already reloaded elsewhere (e.g. loadDocument): align workflow sidebar + history without a second document fetch. */
  const refreshWorkflowPanelOnly = useCallback(async (docId: number) => {
    const [actionsData, historyData] = await Promise.all([
      documentService.getWorkflowAvailableActions(docId),
      documentWorkflowHistoryService.getWorkflowHistory(docId),
    ])
    setWorkflowAvailableActions(workflowAvailableActionsFromApi(actionsData))
    setWorkflowHistory(historyData)
  }, [])

  const loadMetadata = useCallback(async () => {
    try {
      const meta = await documentService.getMetadata(documentId)
      setMetadata(meta)
    } catch (error) {}
  }, [documentId])

  const loadSuggestions = useCallback(async () => {
    try {
      const sugg = await documentService.getSuggestions(documentId)
      setSuggestions(sugg)
    } catch (error) {}
  }, [documentId])

  // Track previous document ID to detect changes (like React filter)
  const prevDocumentIdRef = useRef<number | null>(null)

  // Subscribe to document list service updates to enable/disable navigation buttons
  useEffect(() => {
    const updateDocumentsListState = () => {
      setDocumentsListState({
        documents: documentListViewService.documents,
        isReloading: documentListViewService.isReloading,
        updateCount: documentListViewService.updateCount,
      })
    }

    // Subscribe to service updates
    const unsubscribe = documentListViewService.subscribe(updateDocumentsListState)

    // Initial update
    updateDocumentsListState()

    return unsubscribe
  }, [])

  // Effect 1: Load document ONLY when document ID changes (not section)
  // This matches React's first subscription with filter for id change
  useEffect(() => {
    if (documentId && documentId > 0) {
      const prevId = prevDocumentIdRef.current

      // Only reload document if ID actually changed (like React filter)
      if (prevId !== documentId) {
        prevDocumentIdRef.current = documentId

        // Reset version warning flag when document ID changes
        lastDocumentIdWarning.current = null

        loadDocument()
        loadMetadata()
        loadSuggestions()

        // Ensure documents list is loaded for navigation (next/previous buttons)
        // This ensures the list exists even if user navigated directly to document URL
        if (
          !documentListViewService.documents ||
          documentListViewService.documents.length === 0 ||
          !documentListViewService.documents.find((d) => d.id === documentId)
        ) {
          documentListViewService.reload()
        }
      }

      // If no section in URL, navigate to details (like React)
      if (!section && documentId > 0) {
        navigate(`/documents/${documentId}/details`, { replace: true })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, navigate]) // Only depend on documentId - section changes won't trigger reload

  // Section changes are handled automatically - activeTab is computed from section in render
  // No separate effect needed - this ensures smooth tab switching without reloading

  // Load preview when document loads
  useEffect(() => {
    if (document) {
      loadPreview()
    }
  }, [document?.id, document?.modified, loadPreview]) // Reload after saving a new document version as well as when opening another document

  useEffect(() => {
    accessOpenedLoggedForId.current = null
  }, [documentId])

  useEffect(() => {
    if (!document || loading || document.id !== documentId) return
    if (accessOpenedLoggedForId.current === documentId) return
    accessOpenedLoggedForId.current = documentId
    void documentService
      .recordDocumentAccessEvent(documentId, 'opened')
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: ['document-history', documentId] })
      })
      .catch((err) => {
        console.warn('Failed to record open audit event', err)
      })
  }, [document, documentId, loading, queryClient])

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (previewObjectUrl) {
        URL.revokeObjectURL(previewObjectUrl)
      }
    }
  }, [previewObjectUrl])

  useEffect(() => {
    void loadCorrespondents()
    void loadDocumentTypes()
    void loadWorkflowStatuses()
    void loadStoragePaths()
    void loadTags()
  }, [])

  // Učitaj dostupne workflow akcije (start workflow / definicije)
  useEffect(() => {
    if (!document?.id) {
      setWorkflowAvailableActions(null)
      return
    }
    let cancelled = false
    documentService
      .getWorkflowAvailableActions(document.id)
      .then((actionsData) => {
        if (!cancelled) {
          setWorkflowAvailableActions(workflowAvailableActionsFromApi(actionsData))
        }
      })
      .catch(() => {
        if (!cancelled) setWorkflowAvailableActions(null)
      })
    return () => {
      cancelled = true
    }
  }, [document?.id])

  // Učitaj workflow history (stavka 24 – audit)
  useEffect(() => {
    if (!document?.id) {
      setWorkflowHistory([])
      return
    }
    setWorkflowHistoryLoading(true)
    documentWorkflowHistoryService
      .getWorkflowHistory(document.id)
      .then(setWorkflowHistory)
      .catch(() => setWorkflowHistory([]))
      .finally(() => setWorkflowHistoryLoading(false))
  }, [document?.id])

  const clearWorkflowRequiredFieldError = useCallback((kind: WorkflowRequiredAttributeDetail['kind'], key: number | string) => {
    setWorkflowRequiredFieldErrors((previous) => {
      const customFields = new Set(previous.customFields)
      const systemFields = new Set(previous.systemFields)
      if (kind === 'custom_field' && typeof key === 'number') {
        customFields.delete(key)
      }
      if (kind === 'system_field' && typeof key === 'string') {
        systemFields.delete(key)
      }
      return { customFields, systemFields }
    })
  }, [])

  const setWorkflowRequiredFieldErrorsFromResponse = useCallback((details: unknown) => {
    const customFields = new Set<number>()
    const systemFields = new Set<string>()
    if (Array.isArray(details)) {
      details.forEach((detail: WorkflowRequiredAttributeDetail) => {
        if (detail?.kind === 'custom_field' && typeof detail.field_id === 'number') {
          customFields.add(detail.field_id)
        }
        if (detail?.kind === 'system_field' && typeof detail.system_field === 'string') {
          systemFields.add(detail.system_field)
        }
      })
    }
    setWorkflowRequiredFieldErrors({ customFields, systemFields })
  }, [])

  useEffect(() => {
    setWorkflowRequiredFieldErrors({
      customFields: new Set<number>(),
      systemFields: new Set<string>(),
    })
  }, [documentId])

  const hasUnsavedDocumentChanges = useCallback(() => {
    if (isDirty) return true
    if (document && openDocumentsService.isDirty(document)) return true
    return false
  }, [isDirty, document])

  const workflowAdvanceErrorMessage = useCallback(
    (err: { response?: { data?: { code?: string; detail?: string } } }) => {
      const code = err?.response?.data?.code
      const detail = err?.response?.data?.detail
      if (code === 'workflow_step_assignees_unresolved') {
        return t('document_workflow.errors.workflow_step_assignees_unresolved')
      }
      if (code === 'step_entry_condition_failed') {
        const match = typeof detail === 'string' ? detail.match(/#(\d+)/) : null
        const num = match ? parseInt(match[1], 10) : 1
        return t('document_workflow.errors.step_entry_condition_not_satisfied', { num })
      }
      if (code === 'action_branch_invalid') {
        return detail || t('document_workflow.errors.action_branch_invalid')
      }
      if (code === 'workflow_required_attributes_missing') {
        return detail || t('document_workflow.errors.required_attribute')
      }
      return detail || t('document_workflow.errors.advancing')
    },
    [t]
  )

  const executeWorkflowAdvance = useCallback(
    async (actionId: number, comment?: string) => {
      if (!document?.id) return
      try {
        await documentService.workflowAdvance(document.id, { action_id: actionId, comment: comment ?? '' })
        setWorkflowRequiredFieldErrors({ customFields: new Set<number>(), systemFields: new Set<string>() })
        toastService.showSuccess(t('document_workflow.advanced'))
        await refreshDocumentWorkflowBundle(document.id)
      } catch (err: any) {
        if (err?.response?.data?.code === 'workflow_required_attributes_missing') {
          setWorkflowRequiredFieldErrorsFromResponse(err.response.data.attribute_details)
        }
        toastService.showError(workflowAdvanceErrorMessage(err))
      }
    },
    [document?.id, t, refreshDocumentWorkflowBundle, setWorkflowRequiredFieldErrorsFromResponse, workflowAdvanceErrorMessage]
  )

  const executeStartWorkflow = useCallback(
    async (definitionId: number) => {
      if (!document?.id) return
      try {
        await documentService.workflowAdvance(document.id, { start_workflow_definition_id: definitionId })
        toastService.showSuccess(t('document_workflow.started'))
        await refreshDocumentWorkflowBundle(document.id)
        setShowStartWorkflowChoiceModal(false)
        setShowWorkflowStepperModal(true)
      } catch (err: any) {
        toastService.showError(workflowAdvanceErrorMessage(err))
      }
    },
    [document?.id, t, refreshDocumentWorkflowBundle, workflowAdvanceErrorMessage]
  )

  const handleStepperWorkflowAction = useCallback(
    async (actionId: number, comment?: string) => {
      if (!document?.id) return
      if (hasUnsavedDocumentChanges()) {
        setPendingWorkflowContinuation({ type: 'action', actionId, comment })
        setShowUnsavedWorkflowDialog(true)
        return
      }
      await executeWorkflowAdvance(actionId, comment)
    },
    [document?.id, hasUnsavedDocumentChanges, executeWorkflowAdvance]
  )

  const handleStartWorkflowDefinition = useCallback(
    async (definitionId: number) => {
      if (!document?.id) return
      if (hasUnsavedDocumentChanges()) {
        setPendingWorkflowContinuation({ type: 'start', definitionId })
        setShowUnsavedWorkflowDialog(true)
        return
      }
      await executeStartWorkflow(definitionId)
    },
    [document?.id, hasUnsavedDocumentChanges, executeStartWorkflow]
  )

  const openDocumentWorkflowPopup = useCallback(() => {
    if (workflowAvailableActions?.in_workflow) {
      setShowWorkflowStepperModal(true)
    } else {
      setShowStartWorkflowChoiceModal(true)
    }
  }, [workflowAvailableActions?.in_workflow])

  /** Show the workflow shortcut only when there is something to open (active workflow or startable definitions). */
  const canOpenDocumentWorkflowPopup =
    (workflowAvailableActions?.in_workflow ?? false) ||
    (workflowAvailableActions?.available_definitions?.length ?? 0) > 0

  // Extract document type ID for stable dependency comparison
  const documentTypeId = useMemo(() => {
    return formData.document_type
      ? typeof formData.document_type === 'object'
        ? formData.document_type.id
        : formData.document_type
      : document?.document_type
        ? typeof document.document_type === 'object'
          ? document.document_type.id
          : document.document_type
        : null
  }, [formData.document_type, document?.document_type])

  const activeDocumentType = useMemo(() => {
    if (!documentTypeId) return null
    return documentTypes.find((dt) => dt.id === documentTypeId) || null
  }, [documentTypes, documentTypeId])

  const customFieldConfigById = useMemo(() => {
    const map = new Map<number, any>()
    ;(activeDocumentType?.custom_field_configs || []).forEach((cfg: any) => {
      map.set(cfg.custom_field, cfg)
    })
    return map
  }, [activeDocumentType])

  const customFieldValuesMap = useMemo(() => {
    const map: Record<number, any> = {}
    ;(document?.custom_fields || []).forEach((cfi: any) => {
      map[cfi.field] = cfi.value
    })
    return map
  }, [document?.custom_fields])

  const hiddenCustomFieldIds = useMemo(() => {
    const configs = (activeDocumentType?.custom_field_configs || []) as any[]
    return computeHiddenFieldIds(
      configs.map((cfg) => ({ custom_field: cfg.custom_field, visibility_query: cfg.visibility_query })),
      customFieldValuesMap
    )
  }, [activeDocumentType, customFieldValuesMap])

  const canViewTags = permissionsService.currentUserCan(PermissionAction.View, PermissionType.Tag)
  const tagOptionsFallback = useMemo(() => {
    if (canViewTags || !document?.tags_display || document.tags_display.length === 0) {
      return undefined
    }
    return document.tags_display.map((tag) => ({
      value: tag.id,
      label: tag.name,
      color: tag.color,
      text_color: tag.text_color,
      parent: tag.parent,
    }))
  }, [canViewTags, document?.tags_display])

  const selectedCorrespondentId = typeof formData.correspondent === 'object' ? formData.correspondent?.id : formData.correspondent
  const selectedDocumentTypeId = typeof formData.document_type === 'object' ? formData.document_type?.id : formData.document_type
  const selectedStoragePathId = typeof formData.storage_path === 'object' ? formData.storage_path?.id : formData.storage_path

  const correspondentDisplayFallback = useMemo(() => {
    if (!document?.correspondent_display || !selectedCorrespondentId) return null
    return document.correspondent_display.id === selectedCorrespondentId ? document.correspondent_display : null
  }, [document?.correspondent_display, selectedCorrespondentId])

  const documentTypeDisplayFallback = useMemo(() => {
    if (!document?.document_type_display || !selectedDocumentTypeId) return null
    return document.document_type_display.id === selectedDocumentTypeId ? document.document_type_display : null
  }, [document?.document_type_display, selectedDocumentTypeId])

  const storagePathDisplayFallback = useMemo(() => {
    if (!document?.storage_path_display || !selectedStoragePathId) return null
    return document.storage_path_display.id === selectedStoragePathId ? document.storage_path_display : null
  }, [document?.storage_path_display, selectedStoragePathId])

  const correspondentOptions = useMemo(() => {
    const items = [...correspondents]
    if (selectedCorrespondentId && !items.some((c) => c.id === selectedCorrespondentId)) {
      items.unshift({
        id: selectedCorrespondentId,
        name: correspondentDisplayFallback?.name || `${t('correspondent')} #${selectedCorrespondentId}`,
      } as Correspondent)
    }
    return items
  }, [correspondents, selectedCorrespondentId, correspondentDisplayFallback?.name, t])

  const documentTypeOptions = useMemo(() => {
    const items = [...documentTypes]
    if (selectedDocumentTypeId && !items.some((dt) => dt.id === selectedDocumentTypeId)) {
      items.unshift({
        id: selectedDocumentTypeId,
        name: documentTypeDisplayFallback?.name || `${t('document_type')} #${selectedDocumentTypeId}`,
      } as DocumentType)
    }
    return items
  }, [documentTypes, selectedDocumentTypeId, documentTypeDisplayFallback?.name, t])

  const storagePathOptions = useMemo(() => {
    const items = [...storagePaths]
    if (selectedStoragePathId && !items.some((sp) => sp.id === selectedStoragePathId)) {
      items.unshift({
        id: selectedStoragePathId,
        name: storagePathDisplayFallback?.name || `${t('storage_path')} #${selectedStoragePathId}`,
      } as StoragePath)
    }
    return items
  }, [storagePaths, selectedStoragePathId, storagePathDisplayFallback?.name, t])

  const permissionUsersFallback = useMemo(() => {
    if (!document?.permissions_display) return []
    const unique = new Map<number, { id: number; username: string }>()
    ;[...document.permissions_display.view.users, ...document.permissions_display.change.users].forEach((user) => {
      const display = user.username || [user.first_name, user.last_name].filter(Boolean).join(' ') || `user #${user.id}`
      unique.set(user.id, { id: user.id, username: display })
    })
    return [...unique.values()]
  }, [document?.permissions_display])

  const permissionGroupsFallback = useMemo(() => {
    if (!document?.permissions_display) return []
    const unique = new Map<number, { id: number; name: string }>()
    ;[...document.permissions_display.view.groups, ...document.permissions_display.change.groups].forEach((group) => {
      unique.set(group.id, { id: group.id, name: group.name })
    })
    return [...unique.values()]
  }, [document?.permissions_display])

  useEffect(() => {
    if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.CustomField)) {
      setAllCustomFields([])
      return
    }
    if (documentTypes.length > 0) {
      loadCustomFields()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.id, documentTypes.length, documentTypeId])

  const loadCustomFields = async () => {
    if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.CustomField)) {
      setAllCustomFields([])
      return
    }
    try {
      const response = await customFieldService.listAll()
      const allFields = response.results || []

      setAllCustomFields(allFields)
    } catch (e) {
      setAllCustomFields([])
    }
  }

  const loadCorrespondents = async () => {
    if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.Correspondent)) {
      setCorrespondents([])
      return
    }
    try {
      const response = await correspondentService.listAll()
      setCorrespondents(response.results)
    } catch (e) {}
  }

  const loadDocumentTypes = async () => {
    if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.DocumentType)) {
      setDocumentTypes([])
      return
    }
    try {
      const response = await documentTypeService.listAll()
      setDocumentTypes(response.results)
    } catch (e) {}
  }

  const loadWorkflowStatuses = async () => {
    if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.WorkflowStatus)) {
      setWorkflowStatuses([])
      return
    }
    try {
      const response = await workflowStatusService.listAll()
      setWorkflowStatuses(response.results || [])
    } catch (e) {}
  }

  const loadStoragePaths = async () => {
    if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.StoragePath)) {
      setStoragePaths([])
      return
    }
    try {
      const response = await storagePathService.listAll()
      setStoragePaths(response.results)
    } catch (e) {}
  }

  const loadTags = async () => {
    if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.Tag)) {
      setTags([])
      return
    }
    try {
      const tags = await tagService.listAll()
      setTags(tags)
    } catch (e) {}
  }

  const handleAddCustomField = (field: CustomField) => {
    if (!document) return
    if (!userCanEdit || !workflowActionAllowed('add_attribute')) return

    // Check if field already exists
    const existingField = document.custom_fields?.find((f) => f.field === field.id)
    if (existingField) {
      return
    }

    const newInstance: CustomFieldInstance = {
      field: field.id,
      value: null,
      document: documentId,
      created: new Date().toISOString(),
    }

    const updatedDocument = {
      ...document,
      custom_fields: [...(document.custom_fields || []), newInstance],
    }

    setDocument(updatedDocument)
    setIsDirty(true)
    if (document) {
      openDocumentsService.setDirty(document, true)
    }
  }

  const handleRemoveCustomField = (fieldInstance: CustomFieldInstance) => {
    if (!document) return
    if (!userCanEdit || !workflowCanEditField(fieldInstance.field)) return

    const updatedFields = document.custom_fields.filter((f) => f.field !== fieldInstance.field)

    const updatedDocument = {
      ...document,
      custom_fields: updatedFields,
    }

    setDocument(updatedDocument)
    setIsDirty(true)
    if (document) {
      openDocumentsService.setDirty(document, true)
    }
  }

  const handleCustomFieldValueChange = (fieldId: number, value: any) => {
    if (!document) return
    if (!userCanEdit || !workflowCanEditField(fieldId)) return
    clearWorkflowRequiredFieldError('custom_field', fieldId)

    const fieldExists = document.custom_fields.some((f) => f.field === fieldId)
    const updatedFields = fieldExists
      ? document.custom_fields.map((f) => (f.field === fieldId ? { ...f, value } : f))
      : [...document.custom_fields, { field: fieldId, value } as CustomFieldInstance]

    const updatedDocument = {
      ...document,
      custom_fields: updatedFields,
    }

    setDocument(updatedDocument)
    setIsDirty(true)
    if (document) {
      openDocumentsService.setDirty(document, true)
    }
  }

  const refreshCustomFields = () => {
    loadCustomFields()
  }

  const getCustomFieldFromInstance = (instance: CustomFieldInstance): CustomField | undefined => {
    return allCustomFields.find((f) => f.id === instance.field) || (instance.field_display as CustomField | undefined)
  }

  const getCustomFieldMeta = (field: CustomField) => {
    const cfg = customFieldConfigById.get(field.id)
    const instanceRequired = !!document?.custom_fields?.find((cf) => cf.field === field.id)?.required
    return {
      label: cfg?.override_name || field.name,
      placeholder: cfg?.override_placeholder || field.placeholder || '',
      required: document?.workflow_permissions?.attributes?.[String(field.id)]?.required ?? (!!cfg?.required || instanceRequired),
      order: cfg?.order ?? Number.MAX_SAFE_INTEGER,
    }
  }

  const isCustomFieldValueEmpty = (value: any): boolean => {
    if (value === null || value === undefined) return true
    if (typeof value === 'string') return value.trim() === ''
    if (Array.isArray(value)) return value.length === 0
    return false
  }

  const getTag = (id: number): Tag | undefined => {
    return tags.find((tag) => tag.id === id)
  }

  const addParents = (tagIds: number[]): number[] => {
    const result = new Set(tagIds)
    tagIds.forEach((tagId) => {
      const tag = getTag(tagId)
      if (tag?.parent) {
        const parent = getTag(tag.parent)
        if (parent && !result.has(parent.id)) {
          result.add(parent.id)
          const parentChain = addParents([parent.id])
          parentChain.forEach((id) => result.add(id))
        }
      }
    })
    return Array.from(result)
  }

  const removeChildren = (tagIDs: number[], tag: Tag): number[] => {
    const childTags = tags.filter((t) => t.parent === tag.id)
    if (childTags.length > 0) {
      const childIDs = childTags.map((child) => child.id)
      tagIDs = tagIDs.filter((id) => !childIDs.includes(id))
      for (const child of childTags) {
        tagIDs = removeChildren(tagIDs, child)
      }
    }
    return tagIDs
  }

  const handleFieldChange = (field: string, value: any) => {
    if (!userCanEdit) {
      return
    }
    if (['title', 'archive_serial_number', 'created', 'correspondent', 'document_type', 'storage_path', 'tags'].includes(field)) {
      clearWorkflowRequiredFieldError('system_field', field)
    }
    if (field === 'tags' && Array.isArray(value)) {
      value = addParents(value)
    }

    // If document_type is being changed to a non-null value, show confirmation dialog if document has custom fields
    if (field === 'document_type' && value !== null && value !== undefined) {
      const hasCustomFields = document?.custom_fields && document.custom_fields.length > 0

      if (hasCustomFields) {
        setPendingDocumentTypeChange(value)
        setDocumentTypeChangeCustomFieldsMode('keep')
        setShowDocumentTypeChangeDialog(true)
        return
      }
    }

    // If setting to null or no custom fields, apply directly
    applyDocumentTypeChange(field, value)
  }

  const applyDocumentTypeChange = (field: string, value: any, customFieldsMode: 'keep' | 'replace' = 'keep') => {
    if (field === 'document_type' && document) {
      const currentFields = document.custom_fields || []
      const baseFields = customFieldsMode === 'replace' ? [] : currentFields

      if (value) {
        const documentTypeId = typeof value === 'object' ? value.id : value
        const documentType = documentTypes.find((dt) => dt.id === documentTypeId)

        if (documentType?.custom_fields && documentType.custom_fields.length > 0) {
          // Layout-only attributes (separators) never produce CustomFieldInstance rows.
          const separatorFieldIds = new Set(
            (documentType.custom_field_configs || [])
              .filter((cfg: any) => cfg.custom_field_data_type === 'separator')
              .map((cfg: any) => cfg.custom_field as number),
          )
          const valueStoringFieldIds = documentType.custom_fields.filter(
            (fieldId) => !separatorFieldIds.has(fieldId),
          )
          const updatedCustomFields: CustomFieldInstance[] =
            customFieldsMode === 'replace'
              ? valueStoringFieldIds.map((fieldId) => ({
                  field: fieldId,
                  value: null,
                  document: documentId,
                  created: new Date().toISOString(),
                }))
              : (() => {
                  const existingFieldIds = new Set(baseFields.map((cf: CustomFieldInstance) => cf.field))
                  const fieldsToAdd = valueStoringFieldIds.filter((fieldId) => !existingFieldIds.has(fieldId))
                  const newInstances: CustomFieldInstance[] = fieldsToAdd.map((fieldId) => ({
                    field: fieldId,
                    value: null,
                    document: documentId,
                    created: new Date().toISOString(),
                  }))
                  return [...baseFields, ...newInstances]
                })()

          const updatedDocument = {
            ...document,
            custom_fields: updatedCustomFields,
          }

          setDocument(updatedDocument)

          const updatedData = {
            ...formData,
            [field]: value,
            custom_fields: updatedCustomFields,
          }
          setFormData(updatedData)
          setIsDirty(true)
          if (document) {
            openDocumentsService.setDirty(document, true)
            openDocumentsService.updateDocumentFields(documentId, updatedData)
          }
          return
        } else {
          // Document type has no custom fields
          const updatedDocument = {
            ...document,
            custom_fields: baseFields,
          }

          setDocument(updatedDocument)

          const updatedData = {
            ...formData,
            [field]: value,
            custom_fields: baseFields,
          }
          setFormData(updatedData)
          setIsDirty(true)
          if (document) {
            openDocumentsService.setDirty(document, true)
            openDocumentsService.updateDocumentFields(documentId, updatedData)
          }
          return
        }
      } else {
        // Setting document_type to null, keep current fields
        const updatedDocument = {
          ...document,
          custom_fields: currentFields,
        }

        setDocument(updatedDocument)

        const updatedData = {
          ...formData,
          [field]: value,
          custom_fields: currentFields,
        }
        setFormData(updatedData)
        setIsDirty(true)
        if (document) {
          openDocumentsService.setDirty(document, true)
          openDocumentsService.updateDocumentFields(documentId, updatedData)
        }
        return
      }
    }

    const updatedData = { ...formData, [field]: value }
    setFormData(updatedData)
    setIsDirty(true)
    if (document) {
      openDocumentsService.setDirty(document, true)
      openDocumentsService.updateDocumentFields(documentId, updatedData)
    }
  }

  const handleDocumentTypeChangeConfirm = () => {
    if (pendingDocumentTypeChange !== null) {
      applyDocumentTypeChange('document_type', pendingDocumentTypeChange, documentTypeChangeCustomFieldsMode)
      setPendingDocumentTypeChange(null)
    }
    setShowDocumentTypeChangeDialog(false)
  }

  const handleDocumentTypeChangeCancel = () => {
    setPendingDocumentTypeChange(null)
    setDocumentTypeChangeCustomFieldsMode('keep')
    setShowDocumentTypeChangeDialog(false)
  }

  const handlePermissionsFormChange = (permissions: PermissionsFormValue) => {
    if (!userCanEdit) return
    setPermissionsForm(permissions)
    setIsDirty(true)
    // Update formData with permissions (use any to bypass type check for API fields)
    const updatedData: any = {
      ...formData,
      owner: permissions.owner,
      set_permissions: permissions.set_permissions,
    }
    setFormData(updatedData)
    if (document) {
      openDocumentsService.setDirty(document, true)
      openDocumentsService.updateDocumentFields(documentId, updatedData)
    }
  }

  const save = async (closeAfter = false): Promise<boolean> => {
    if (!userCanEdit) {
      toastService.showError(t('error_saving_document_no_permission'))
      return false
    }
    try {
      if (document) {
        const requiredConfigs = (activeDocumentType?.custom_field_configs || []).filter((cfg: any) => cfg.required)
        const missingFromType = requiredConfigs.filter((cfg: any) => {
          if (cfg.custom_field_data_type === 'counter') {
            return false
          }
          if (hiddenCustomFieldIds.has(cfg.custom_field)) {
            return false
          }
          const fieldInstance = document.custom_fields?.find((cf) => cf.field === cfg.custom_field)
          return isCustomFieldValueEmpty(fieldInstance?.value)
        })
        const typeRequiredFieldIds = new Set(requiredConfigs.map((cfg: any) => cfg.custom_field))
        const missingFromInstance = (document.custom_fields || []).filter((cf) => {
          if (!cf.required || typeRequiredFieldIds.has(cf.field)) {
            return false
          }
          const field = getCustomFieldFromInstance(cf)
          if (field?.data_type === 'counter') {
            return false
          }
          return isCustomFieldValueEmpty(cf.value)
        })
        if (missingFromType.length > 0 || missingFromInstance.length > 0) {
          const missingNames = [
            ...missingFromType.map((cfg: any) => {
              const field = allCustomFields.find((item) => item.id === cfg.custom_field)
              return cfg.override_name || cfg.custom_field_name || field?.name || String(cfg.custom_field)
            }),
            ...missingFromInstance.map((cf) => {
              const field = getCustomFieldFromInstance(cf)
              return field?.name || String(cf.field)
            }),
          ].filter(Boolean)
          toastService.showError(
            `${t('error_saving_document')}: ${t('missing_required_attributes')}: ${missingNames.join(', ')}`,
          )
          return false
        }
      }
      setSaving(true)
      // Map permissions_form back to document format before saving
      const dataToSave: any = {
        ...formData,
        custom_fields: (document?.custom_fields || [])
          .filter((cf: any) => !hiddenCustomFieldIds.has(cf.field))
          .map((cf: any) => {
            if (cf.field_display?.data_type === 'counter') {
              return { field: cf.field }
            }
            return { field: cf.field, value: cf.value }
          }),
        owner: permissionsForm.owner,
        set_permissions: permissionsForm.set_permissions,
      }
      delete dataToSave.workflow_status
      if (settingsService.getBoolean(SETTINGS_KEYS.DOCUMENT_EDITING_REMOVE_INBOX_TAGS, false)) {
        dataToSave.remove_inbox_tags = true
      }
      const updated = await documentService.updateDocument(documentId, dataToSave)

      // Update document in open documents service with fresh version from server
      openDocumentsService.updateDocument(updated)

      setDocument(updated)
      setFormData(updated)

      // Refetch workflow akcije nakon čuvanja (npr. kad se promeni document_type) — await da UI ne meša star i nov odgovor
      try {
        const actionsData = await documentService.getWorkflowAvailableActions(documentId)
        setWorkflowAvailableActions(workflowAvailableActionsFromApi(actionsData))
      } catch {
        setWorkflowAvailableActions(null)
      }

      // Update permissions form from saved document
      const updatedOwnerId = updated.owner ? (typeof updated.owner === 'number' ? updated.owner : updated.owner.id) : null
      const updatedPermissions = (updated as any).permissions || (updated as any).set_permissions
      setPermissionsForm({
        owner: updatedOwnerId,
        set_permissions: updatedPermissions || {
          view: { users: [], groups: [] },
          change: { users: [], groups: [] },
        },
      })
      setIsDirty(false)
      openDocumentsService.setDirty(updated, false)
      setSaving(false)
      toastService.showSuccess(t('document_saved_successfully'))
      for (const value of updated.newly_assigned_counter_values || []) {
        toastService.showInfo(t('counter_assigned_toast', { value }))
      }

      if (closeAfter) {
        close()
      }
      return true
    } catch (error: any) {
      setSaving(false)
      const responseData = error?.response?.data
      const backendDetail =
        typeof responseData?.detail === 'string'
          ? responseData.detail
          : Array.isArray(responseData?.custom_fields)
            ? responseData.custom_fields.filter((value: unknown) => typeof value === 'string').join(', ')
            : ''
      const message = backendDetail
        ? `${t('error_saving_document')}: ${backendDetail}`
        : t('error_saving_document')
      toastService.showError(message, error)
      return false
    }
  }

  const handleConfirmSaveAndContinueWorkflow = useCallback(async () => {
    const pending = pendingWorkflowContinuation
    if (!pending) return
    setWorkflowContinuing(true)
    const ok = await save(false)
    setWorkflowContinuing(false)
    setShowUnsavedWorkflowDialog(false)
    setPendingWorkflowContinuation(null)
    if (!ok) return
    if (pending.type === 'action') {
      await executeWorkflowAdvance(pending.actionId, pending.comment)
    } else {
      await executeStartWorkflow(pending.definitionId)
    }
  }, [pendingWorkflowContinuation, executeWorkflowAdvance, executeStartWorkflow])

  const handleCancelSaveAndContinueWorkflow = useCallback(() => {
    if (workflowContinuing) return
    setShowUnsavedWorkflowDialog(false)
    setPendingWorkflowContinuation(null)
  }, [workflowContinuing])

  const discard = async () => {
    if (!document) return

    try {
      // Fetch fresh document from server
      const freshDoc = await documentService.getDocument(documentId)

      // Update open document with fresh version (replace existing)
      openDocumentsService.updateDocument(freshDoc)

      // Update local state
      setDocument(freshDoc)
      setFormData(freshDoc)
      // Reset permissions form from fresh document
      const freshOwnerId: number | null = freshDoc.owner ? (typeof freshDoc.owner === 'number' ? freshDoc.owner : (freshDoc.owner as any).id) : null
      const freshPermissions: any = (freshDoc as any).permissions || (freshDoc as any).set_permissions
      setPermissionsForm({
        owner: freshOwnerId,
        set_permissions: freshPermissions || {
          view: { users: [], groups: [] },
          change: { users: [], groups: [] },
        },
      })
      setIsDirty(false)
      openDocumentsService.setDirty(freshDoc, false)

      toastService.showInfo(t('changes_discarded'))
    } catch (error) {
      toastService.showError(t('error_loading_document'), error)
    }
  }

  const close = useCallback(() => {
    if (!document) return

    if (openDocumentsService.isDirty(document)) {
      setShowCloseDocumentDialog(true)
    } else {
      openDocumentsService.closeDocument(document)

      if (documentListViewService.activeSavedViewId) {
        navigate(`/view/${documentListViewService.activeSavedViewId}`)
      } else {
        navigate('/documents')
      }
    }
  }, [document, navigate])

  const handleConfirmCloseDocument = useCallback(() => {
    if (!document) return

    openDocumentsService.closeDocument(document)
    setShowCloseDocumentDialog(false)

    if (documentListViewService.activeSavedViewId) {
      navigate(`/view/${documentListViewService.activeSavedViewId}`)
    } else {
      navigate('/documents')
    }
  }, [document, navigate])

  const handleCancelCloseDocument = useCallback(() => {
    setShowCloseDocumentDialog(false)
  }, [])

  const hasNext = useCallback((): boolean => {
    if (!document) return false

    const docs = documentsListState.documents
    if (!docs || docs.length === 0) return false

    const index = docs.findIndex((d) => d.id === document.id)
    if (index === -1) return false

    return documentListViewService.hasNext(document.id)
  }, [document, documentsListState])

  const hasPrevious = useCallback((): boolean => {
    if (!document) return false

    const docs = documentsListState.documents
    if (!docs || docs.length === 0) return false

    const index = docs.findIndex((d) => d.id === document.id)
    if (index === -1) return false

    return documentListViewService.hasPrevious(document.id)
  }, [document, documentsListState])

  const nextDoc = useCallback(async () => {
    if (!document) return
    const nextDocId = await documentListViewService.getNext(document.id)
    if (nextDocId) {
      navigate(`/documents/${nextDocId}/details`)
    }
  }, [document, navigate])

  const previousDoc = useCallback(async () => {
    if (!document) return
    const prevDocId = await documentListViewService.getPrevious(document.id)
    if (prevDocId) {
      navigate(`/documents/${prevDocId}/details`)
    }
  }, [document, navigate])

  /**
   * Escape na detaljima: ako je otvoren bilo koji Bootstrap modal (uključujući u child komponentama),
   * ne zatvaraj ceo dokument — samo modal (keyboard na Modal-u). Capture faza upisuje stanje pre bubble.
   */
  const skipEscapeCloseDocumentRef = useRef(false)

  // Keyboard shortcuts (like React)
  useEffect(() => {
    const handleEscapeCapture = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      skipEscapeCloseDocumentRef.current = !!window.document.querySelector('.modal.show')
    }

    const handleKeyDownWindow = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (skipEscapeCloseDocumentRef.current) {
          skipEscapeCloseDocumentRef.current = false
          return
        }
        close()
        return
      }

      // Ctrl+ArrowRight - Next document
      if (e.ctrlKey && e.key === 'ArrowRight') {
        e.preventDefault()
        if (hasNext()) {
          nextDoc()
        }
        return
      }

      // Ctrl+ArrowLeft - Previous document
      if (e.ctrlKey && e.key === 'ArrowLeft') {
        e.preventDefault()
        if (hasPrevious()) {
          previousDoc()
        }
        return
      }
    }

    window.document.addEventListener('keydown', handleEscapeCapture, true)
    window.addEventListener('keydown', handleKeyDownWindow)
    return () => {
      window.document.removeEventListener('keydown', handleEscapeCapture, true)
      window.removeEventListener('keydown', handleKeyDownWindow)
    }
  }, [close, hasNext, hasPrevious, nextDoc, previousDoc])

  const deleteDocument = async () => {
    if (!document || isDeleteSubmitting) return

    const executeDelete = async (permanentDelete?: boolean) => {
      try {
        setIsDeleteSubmitting(true)
        if (permanentDelete) {
          await documentService.bulkEditOperation([documentId], 'delete', { permanent: true })
          toastService.showSuccess(
            t('document_permanently_deleted', { title: document.title || document.original_file_name || 'Untitled' }) || t('document_moved_to_trash')
          )
        } else {
          await documentService.deleteDocument(documentId)
          toastService.showSuccess(t('document_moved_to_trash'))
        }
        await invalidateDocumentQueries()
        setShowConfirmDialog(false)
        close()
      } catch (error: any) {
        const message = error?.response?.status === 403 ? t('error_deleting_document_no_permission') : t('error_deleting_document')
        toastService.showError(message, error)
      } finally {
        setIsDeleteSubmitting(false)
      }
    }

    setConfirmDialogConfig({
      title: t('confirm_delete'),
      message: t('confirm_move_to_trash', { count: 1 }),
      variant: 'danger',
      showPermanentDeleteOption: true,
      permanentDeleteLabel: t('permanent_delete'),
      permanentDeleteDescription: t('permanent_delete_description'),
    })
    setConfirmAction(() => executeDelete)
    setShowConfirmDialog(true)
  }

  const download = async (original = false) => {
    try {
      setDownloading(true)
      const blob = await documentService.downloadDocumentForUserAction(documentId, original)
      const url = URL.createObjectURL(blob)
      const link = window.document.createElement('a')
      link.href = url
      link.download = original
        ? document?.original_file_name || document?.title || 'document'
        : document?.archived_file_name || document?.title || 'document.pdf'
      link.click()
      URL.revokeObjectURL(url)
      void queryClient.invalidateQueries({ queryKey: ['document-history', documentId] })
      setDownloading(false)
    } catch (error) {
      setDownloading(false)
      toastService.showError('Error downloading document', error)
    }
  }

  const userIsOwner = React.useMemo(() => {
    if (!document) return true
    return permissionsService.currentUserOwnsObject(document as any)
  }, [document])

  const userCanEdit = React.useMemo(() => {
    if (!document) return true
    return (
      permissionsService.currentUserCan(PermissionAction.Change, PermissionType.Document) &&
      permissionsService.currentUserHasObjectPermissions(PermissionAction.Change, document as any)
    )
  }, [document])

  const workflowActionAllowed = useCallback(
    (action: string) => document?.workflow_permissions?.actions?.[action] ?? true,
    [document],
  )

  const workflowCanEditField = useCallback(
    (fieldId: number) => document?.workflow_permissions?.attributes?.[String(fieldId)]?.editable ?? true,
    [document],
  )

  const workflowCanViewSystemField = useCallback(
    (field: string) => document?.workflow_permissions?.system_attributes?.[field]?.visible ?? true,
    [document],
  )

  const workflowCanEditSystemField = useCallback(
    (field: string) => document?.workflow_permissions?.system_attributes?.[field]?.editable ?? true,
    [document],
  )

  const workflowSystemFieldRequired = useCallback(
    (field: string) => document?.workflow_permissions?.system_attributes?.[field]?.required ?? false,
    [document],
  )

  /** Iste kao backend: mutacije beleški zahtevaju i izmenu dokumenta (model + objekat). */
  const userCanMutateNotesOnDocument = React.useMemo(() => {
    if (!document) return false
    return (
      permissionsService.currentUserCan(PermissionAction.Change, PermissionType.Document) &&
      permissionsService.currentUserHasObjectPermissions(PermissionAction.Change, document as any)
    )
  }, [document])

  const userCanAddNote = React.useMemo(() => {
    if (!document) return false
    return (
      permissionsService.currentUserCan(PermissionAction.Add, PermissionType.Note) && userCanMutateNotesOnDocument
    )
  }, [document, userCanMutateNotesOnDocument])

  const userCanDeleteNote = React.useMemo(() => {
    if (!document) return false
    return (
      permissionsService.currentUserCan(PermissionAction.Delete, PermissionType.Note) &&
      userCanMutateNotesOnDocument
    )
  }, [document, userCanMutateNotesOnDocument])

  const userCanEditNoteContent = React.useMemo(() => {
    if (!document) return false
    return (
      permissionsService.currentUserCan(PermissionAction.Change, PermissionType.Note) &&
      userCanMutateNotesOnDocument
    )
  }, [document, userCanMutateNotesOnDocument])

  const canViewHistory = permissionsService.currentUserCan(PermissionAction.View, PermissionType.History)
  const canViewNotes = permissionsService.currentUserCan(PermissionAction.View, PermissionType.Note)
  const canViewShareLinks = permissionsService.currentUserCan(PermissionAction.View, PermissionType.ShareLink)
  const { data: notes = [] } = useQuery<DocumentNote[]>({
    queryKey: ['document-notes', documentId],
    queryFn: () => documentNotesService.getNotes(documentId),
    enabled: canViewNotes && Boolean(documentId),
  })

  // Redirect if user tries to access a tab they don't have permission for; legacy workflow-history/esign-audit -> history
  useEffect(() => {
    if (activeTab === DocumentDetailNavIDs.History && !canViewHistory) {
      navigate(`/documents/${documentId}/details`, { replace: true })
    } else if (activeTab === DocumentDetailNavIDs.WorkflowHistory) {
      setHistorySubTab('workflow')
      navigate(`/documents/${documentId}/history`, { replace: true })
    } else if (activeTab === DocumentDetailNavIDs.ESignAudit) {
      setHistorySubTab('esign')
      navigate(`/documents/${documentId}/history`, { replace: true })
    } else if (activeTab === DocumentDetailNavIDs.Notes && !canViewNotes) {
      navigate(`/documents/${documentId}/details`, { replace: true })
    } else if (activeTab === DocumentDetailNavIDs.ESignAudit && !settingsService.getBoolean('docusign_enabled', false)) {
      navigate(`/documents/${documentId}/details`, { replace: true })
    }
  }, [activeTab, canViewHistory, canViewNotes, documentId, navigate])

  const reprocess = async () => {
    if (!document) return

    const executeReprocess = async () => {
      try {
        await documentService.bulkEditOperation([document.id], 'reprocess', {})
        setShowConfirmDialog(false)
        toastService.showInfo(
          t('reprocess_operation_for_will_begin_in_background', { title: document.title }) ||
            `Reprocess operation for "${document.title}" will begin in the background. Close and re-open or reload this document after the operation has completed to see new content.`
        )
      } catch (error) {
        toastService.showError(t('error_executing_operation') || 'Error executing operation', error)
      }
    }

    setConfirmDialogConfig({
      title: t('reprocess_confirm') || 'Reprocess confirm',
      messageBold: t('reprocess_permanently_recreate_archive') || 'This operation will permanently recreate the archive file for this document.',
      message: t('reprocess_archive_regenerated') || 'The archive file will be re-generated with the current settings.',
      confirmText: t('proceed') || 'Proceed',
      variant: 'danger',
    })
    setConfirmAction(() => executeReprocess)
    setShowConfirmDialog(true)
  }

  const openNewVersionDialog = () => {
    setNewVersionFile(null)
    setNewVersionFileName('')
    setNewVersionComment('')
    setShowNewVersionDialog(true)
  }

  const invalidateDocumentQueries = useCallback(async () => {
    await documentListViewService.reload()

    const openDocs = [...openDocumentsService.getOpenDocuments()]
    for (const openDoc of openDocs) {
      try {
        const fresh = await documentService.getDocument(openDoc.id)
        openDocumentsService.updateDocument(fresh)
      } catch (error) {
        try {
          const versions = await documentService.getVersions(openDoc.id)
          const currentVersion = versions.find((v) => v.is_current)
          if (currentVersion) {
            const freshCurrent = await documentService.getDocument(currentVersion.id)
            openDocumentsService.closeDocument(openDoc)
            openDocumentsService.openDocument(freshCurrent)
            if (openDoc.id === documentId) {
              navigate(`/documents/${freshCurrent.id}/${activeTab}`, { replace: true })
            }
          } else {
            openDocumentsService.closeDocument(openDoc)
          }
        } catch {
          openDocumentsService.closeDocument(openDoc)
        }
      }
    }
  }, [activeTab, documentId, navigate])

  const waitForNewCurrentVersion = useCallback(async () => {
    const maxAttempts = 30
    const delayMs = 2000
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const versions = await documentService.getVersions(documentId)
        const currentVersion = versions.find((v) => v.is_current)
        if (currentVersion && currentVersion.id !== documentId) {
          await invalidateDocumentQueries()
          navigate(`/documents/${currentVersion.id}/${DocumentDetailNavIDs.Versions}`, { replace: true })
          return
        }
      } catch (error) {}
      await new Promise((resolve) => setTimeout(resolve, delayMs))
    }
    await invalidateDocumentQueries()
  }, [documentId, invalidateDocumentQueries, navigate])

  const createNewVersion = async () => {
    if (!document) return
    try {
      setCreatingVersion(true)
      if (!newVersionFile) {
        toastService.showError(t('select_file_to_upload'))
        return
      }
      await documentService.uploadVersion(document.id, newVersionFile, newVersionComment)
      toastService.showInfo(t('version_creation_started'))
      setVersionsRefreshKey((prev) => prev + 1)
      setShowNewVersionDialog(false)
      waitForNewCurrentVersion()
    } catch (error) {
      toastService.showError(t('error_creating_version'), error)
    } finally {
      setCreatingVersion(false)
    }
  }

  const refreshTemplate = async () => {
    if (!document || isRefreshTemplateSubmitting) return

    const executeRefreshTemplate = async () => {
      try {
        setIsRefreshTemplateSubmitting(true)
        await documentService.bulkEditOperation([document.id], 'refresh_template', {})
        setShowConfirmDialog(false)
        toastService.showSuccess(t('refresh_template_done') || 'Šablon osvežen.')
        loadDocument(true)
      } catch (error) {
        toastService.showError(t('error_executing_operation') || 'Error executing operation', error)
      } finally {
        setIsRefreshTemplateSubmitting(false)
      }
    }

    setConfirmDialogConfig({
      title: t('refresh_template_confirm_title') || 'Osveži šablon',
      messageBold: t('refresh_template_confirm_bold') || 'Ova akcija će zameniti vrednosti atributa mapiranih na šablon.',
      message:
        t('refresh_template_confirm_message') ||
        'Samo atributi koji se uspešno izdvoje iz dokumenta biće ažurirani. Ostale vrednosti ostaju nepromenjene.',
      confirmText: t('proceed') || 'Nastavi',
      variant: 'warning',
    })
    setConfirmAction(() => executeRefreshTemplate)
    setShowConfirmDialog(true)
  }

  const printDocument = async () => {
    if (!document || !workflowActionAllowed('print')) return
    try {
      const blob = await documentService.downloadDocument(document.id, false)
      const blobUrl = URL.createObjectURL(blob)
      const iframe = window.document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = blobUrl
      window.document.body.appendChild(iframe)
      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus()
          iframe.contentWindow?.print()
          void documentService
            .recordDocumentAccessEvent(document.id, 'printed')
            .then(() => {
              void queryClient.invalidateQueries({ queryKey: ['document-history', document.id] })
            })
            .catch((err) => {
              console.warn('Failed to record print audit event', err)
            })
          iframe.contentWindow!.onafterprint = () => {
            window.document.body.removeChild(iframe)
            URL.revokeObjectURL(blobUrl)
          }
        } catch (err) {
          toastService.showError(t('print_failed') || 'Print failed.', err)
          window.document.body.removeChild(iframe)
          URL.revokeObjectURL(blobUrl)
        }
      }
    } catch (error) {
      toastService.showError(t('error_loading_document_for_printing') || 'Error loading document for printing.', error)
    }
  }

  const moreLike = () => {
    if (!document || !workflowActionAllowed('more_like_this')) return

    const filterRules: FilterRule[] = [
      {
        rule_type: FILTER_FULLTEXT_MORELIKE,
        value: document.id.toString(),
      },
    ]

    // Generate URL query params from filter rules (like React)
    const queryParams = queryParamsFromFilterRules(filterRules)

    // Build query string
    const queryString = new URLSearchParams()
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryString.set(key, value.toString())
      }
    })

    // Navigate with query params - DocumentList will load filters from URL
    // Don't call quickFilter here - it would trigger reload and overwrite URL
    navigate(`/documents?${queryString.toString()}`)
  }

  const openShareLinks = () => {
    setShowShareLinksDialog(true)
  }

  const openEmailDocument = () => {
    if (!workflowActionAllowed('email')) return
    setShowEmailDialog(true)
  }

  const increaseZoom = () => setPdfZoom((prev) => Math.min(prev + 0.25, 3))
  const decreaseZoom = () => setPdfZoom((prev) => Math.max(prev - 0.25, 0.5))

  // Filter documents based on field value
  const filterDocuments = (value: any, ruleType: number) => {
    if (!value) return

    let filterRules: FilterRule[]

    // For tags, create multiple filter rules (one per tag)
    if (ruleType === FILTER_HAS_TAGS_ALL && Array.isArray(value)) {
      filterRules = value.map((tagId) => ({
        rule_type: ruleType,
        value: tagId.toString(),
      }))
    } else {
      // For other fields, create a single filter rule
      filterRules = [
        {
          rule_type: ruleType,
          value: value.toString(),
        },
      ]
    }

    // Navigate to documents list and apply filter
    documentListViewService.quickFilter(filterRules)
    navigate('/documents')
  }

  // Filter by date - same as React (date - 1 day and date + 1 day)
  const filterByDate = (dateValue: string) => {
    const date = new Date(dateValue + 'T12:00:00')
    const dateAfter = new Date(date)
    dateAfter.setDate(dateAfter.getDate() - 1)
    const dateBefore = new Date(date)
    dateBefore.setDate(dateBefore.getDate() + 1)
    const afterStr = dateAfter.toISOString().substring(0, 10)
    const beforeStr = dateBefore.toISOString().substring(0, 10)
    navigate(`/documents?created__date__gt=${afterStr}&created__date__lt=${beforeStr}`)
  }

  // Get next ASN (Archive Serial Number) from backend
  const getNextAsn = async () => {
    if (formData.archive_serial_number) return
    try {
      const nextAsn = await documentService.getNextAsn()
      handleFieldChange('archive_serial_number', nextAsn)
    } catch (error) {}
  }

  // Open create dialogs
  const openCreateCorrespondent = () => setShowCorrespondentDialog(true)
  const openCreateDocumentType = () => setShowDocumentTypeDialog(true)
  const openCreateStoragePath = () => setShowStoragePathDialog(true)
  const openCreateTag = () => setShowTagDialog(true)

  // Handle success callbacks from edit dialogs
  const handleCorrespondentCreated = async (newItem?: Correspondent) => {
    await loadCorrespondents()
    if (newItem) {
      handleFieldChange('correspondent', newItem.id)
    }
    setShowCorrespondentDialog(false)
  }

  const handleDocumentTypeCreated = async (newItem?: DocumentType) => {
    await loadDocumentTypes()
    if (newItem) {
      handleFieldChange('document_type', newItem.id)
    }
    setShowDocumentTypeDialog(false)
  }

  const handleWorkflowStatusCreated = async (newItem?: WorkflowStatus) => {
    await loadWorkflowStatuses()
    if (newItem) {
      handleFieldChange('workflow_status', newItem.id)
    }
    setShowWorkflowStatusDialog(false)
  }

  const handleClaim = async () => {
    setClaimSubmitting(true)
    try {
      await documentService.claimDocument(documentId)
      toastService.showSuccess(t('document_workflow.claim.claimed_success'))
      await loadDocument(true)
      await refreshWorkflowPanelOnly(documentId)
    } catch (e: any) {
      toastService.showError(e?.response?.data?.detail || t('error_advancing_workflow'))
    } finally {
      setClaimSubmitting(false)
    }
  }

  const handleUnclaim = async () => {
    setClaimSubmitting(true)
    try {
      await documentService.unclaimDocument(documentId)
      toastService.showSuccess(t('document_workflow.claim.unclaimed_success'))
      await loadDocument(true)
      await refreshWorkflowPanelOnly(documentId)
    } catch (e: any) {
      toastService.showError(e?.response?.data?.detail || t('error_advancing_workflow'))
    } finally {
      setClaimSubmitting(false)
    }
  }

  const claimedById = document?.claimed_by != null ? (typeof document.claimed_by === 'object' ? document.claimed_by.id : document.claimed_by) : null
  const claimedByUsername = typeof document?.claimed_by === 'object' ? (document.claimed_by as any)?.username : null
  const currentUserId = permissionsService.getCurrentUser()?.id ?? null
  const isClaimedByMe = claimedById !== null && currentUserId !== null && claimedById === currentUserId

  const handleStoragePathCreated = async (newItem?: StoragePath) => {
    await loadStoragePaths()
    if (newItem) {
      handleFieldChange('storage_path', newItem.id)
    }
    setShowStoragePathDialog(false)
  }

  const handleTagCreated = async (newItem?: Tag) => {
    if (newItem) {
      setTags((prev) => [...prev, newItem])

      const currentTags = formData.tags || document?.tags || []
      const newTags = [...currentTags, newItem.id]
      handleFieldChange('tags', newTags)

      loadTags()
    }
    setShowTagDialog(false)
  }

  if (loading || !document) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </div>
    )
  }

  const isPdf = (previewMimeType || document.mime_type) === 'application/pdf'
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i)) + ' ' + sizes[i]
  }

  const displayTitle = document.version_group_title || formData.title || document.title

  return (
    <div className="document-detail">
      <PageHeader title={displayTitle}>
        {/* PDF Zoom controls for large screens */}
        {isPdf && (
          <div className="input-group input-group-sm me-md-5 d-none d-md-flex">
            <button className="btn btn-outline-secondary" onClick={decreaseZoom}>
              -
            </button>
            <select
              className="form-select"
              value={Math.round(pdfZoom * 100)}
              onChange={(e) => setPdfZoom(parseInt(e.target.value) / 100)}
              style={{ width: 'auto' }}
            >
              <option value="50">50%</option>
              <option value="75">75%</option>
              <option value="100">100%</option>
              <option value="125">125%</option>
              <option value="150">150%</option>
              <option value="200">200%</option>
            </select>
            <button className="btn btn-outline-secondary" onClick={increaseZoom}>
              +
            </button>
          </div>
        )}

        {/* Documentation button */}
        <Button
          variant="link"
          size="sm"
          onClick={() => setShowDocsDialog(true)}
          title={t('help')}
          className="p-0 me-2"
          style={{ width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <i className="bi bi-info-circle"></i>
        </Button>

        {/* Delete button */}
        <Button
          variant="outline-danger"
          size="sm"
          onClick={deleteDocument}
          disabled={!userIsOwner || !workflowActionAllowed('delete')}
          className="me-md-4"
          style={{ display: 'inline-flex', alignItems: 'center' }}
        >
          <i className="bi bi-trash" style={{ width: '1.2em', height: '1.2em', display: 'inline-block' }}></i>
          <span className="d-none d-lg-inline ps-1" style={{ display: 'inline-block' }}>
            {t('delete')}
          </span>
        </Button>

        {/* Download button with optional dropdown */}
        {metadata?.has_archive_version ? (
          <Dropdown as={ButtonGroup} className="me-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => download(false)}
              disabled={downloading || !workflowActionAllowed('download')}
              style={{ display: 'inline-flex', alignItems: 'center' }}
              title={t('download')}
            >
              {downloading ? (
                <Spinner animation="border" size="sm" style={{ display: 'inline-block' }} />
              ) : (
                <i className="bi bi-download" style={{ width: '1.2em', height: '1.2em', display: 'inline-block' }}></i>
              )}
              <span className="d-none d-lg-inline ps-1" style={{ display: 'inline-block' }}>
                {t('download')}
              </span>
            </Button>
            <Dropdown.Toggle split variant="outline-primary" size="sm" disabled={downloading || !workflowActionAllowed('download')} />
            <Dropdown.Menu className="shadow">
              <Dropdown.Item onClick={() => download(true)} disabled={!workflowActionAllowed('download')}>{t('download_original')}</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        ) : (
          <Button
            className="me-2"
            variant="outline-primary"
            size="sm"
            onClick={() => download(true)}
            disabled={downloading || !workflowActionAllowed('download')}
            style={{ display: 'inline-flex', alignItems: 'center' }}
            title={t('download')}
          >
            {downloading ? (
              <Spinner animation="border" size="sm" style={{ display: 'inline-block' }} />
            ) : (
              <i className="bi bi-download" style={{ width: '1.2em', height: '1.2em', display: 'inline-block' }}></i>
            )}
            <span className="d-none d-lg-inline ps-1" style={{ display: 'inline-block' }}>
              {t('download')}
            </span>
          </Button>
        )}

        {/* Actions dropdown */}
        <Dropdown className="me-2 ms-auto">
          <Dropdown.Toggle variant="outline-primary" size="sm" id="actionsDropdown">
            <i className="bi bi-three-dots"></i>
            <span className="d-none d-sm-inline">&nbsp;{t('actions')}</span>
          </Dropdown.Toggle>
          <Dropdown.Menu className="shadow">
            <Dropdown.Item onClick={openNewVersionDialog} disabled={!userCanEdit || !userIsOwner || !workflowActionAllowed('new_version')}>
              <i className="bi bi-plus-circle" style={{ width: '1em', height: '1em' }}></i>
              &nbsp;{t('new_version')}
            </Dropdown.Item>
            <Dropdown.Item onClick={reprocess} disabled={!userCanEdit || !userIsOwner || !workflowActionAllowed('reprocess')}>
              <i className="bi bi-arrow-counterclockwise" style={{ width: '1em', height: '1em' }}></i>
              &nbsp;{t('reprocess')}
            </Dropdown.Item>
            <Dropdown.Item
              onClick={refreshTemplate}
              disabled={!userCanEdit || !userIsOwner || !document?.document_type || !workflowActionAllowed('refresh_template')}
              title={
                !document?.document_type
                  ? t('refresh_template_disabled_no_type')
                  : !userCanEdit || !userIsOwner
                    ? t('refresh_template_disabled_no_permission')
                    : undefined
              }
              className={!userCanEdit || !userIsOwner || !document?.document_type ? 'refresh-template-disabled' : ''}
            >
              <i className="bi bi-file-earmark-arrow-down" style={{ width: '1em', height: '1em' }} />
              &nbsp;{t('refresh_template')}
            </Dropdown.Item>
            <Dropdown.Item onClick={printDocument} disabled={!workflowActionAllowed('print')}>
              <i className="bi bi-printer" style={{ width: '1em', height: '1em' }}></i>
              &nbsp;{t('print')}
            </Dropdown.Item>
            <Dropdown.Item onClick={moreLike} disabled={!workflowActionAllowed('more_like_this')}>
              <i className="bi bi-diagram-3" style={{ width: '1em', height: '1em' }}></i>
              &nbsp;{t('more_like_this')}
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setShowPdfEditor(true)} disabled={!userIsOwner || !userCanEdit || !workflowActionAllowed('pdf_editor') || document.mime_type !== 'application/pdf'}>
              <i className="bi bi-pencil" style={{ width: '1em', height: '1em' }}></i>
              &nbsp;{t('pdf_editor')}
            </Dropdown.Item>
            {settingsService.getBoolean('docusign_enabled', false) && (
              <Dropdown.Item onClick={() => setShowDocuSignSendDialog(true)} disabled={!document?.archived_file_name}>
                <i className="bi bi-pen" style={{ width: '1em', height: '1em' }}></i>
                &nbsp;{t('docusign.send.forSignature')}
              </Dropdown.Item>
            )}
            {canOpenDocumentWorkflowPopup && (
              <Dropdown.Item onClick={openDocumentWorkflowPopup}>
                <i className="bi bi-diagram-3" style={{ width: '1em', height: '1em' }}></i>
                &nbsp;{t('document_workflow.title')}
              </Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>

        {/* Custom Fields dropdown */}
        <CustomFieldsDropdown
          documentId={documentId}
          disabled={!userCanEdit || !workflowActionAllowed('add_attribute')}
          existingFields={document?.custom_fields || []}
          onAdded={handleAddCustomField}
          onCreated={refreshCustomFields}
        />

        {/* Send dropdown */}
        <Dropdown className="me-2">
          <Dropdown.Toggle variant="outline-primary" size="sm" id="sendDropdown">
            <i className="bi bi-send"></i>
            <span className="d-none d-sm-inline">&nbsp;{t('send')}</span>
          </Dropdown.Toggle>
          <Dropdown.Menu className="shadow">
            {canViewShareLinks && (
              <Dropdown.Item onClick={openShareLinks} disabled={!workflowActionAllowed('share_link')}>
                <i className="bi bi-link"></i>&nbsp;{t('share_links')}
              </Dropdown.Item>
            )}
            <Dropdown.Item onClick={openEmailDocument} disabled={!workflowActionAllowed('email')}>
              <i className="bi bi-envelope"></i>&nbsp;{t('email')}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </PageHeader>

      <Modal show={showNewVersionDialog} onHide={() => setShowNewVersionDialog(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('new_version')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t('file')}</Form.Label>
              <input
                ref={newVersionFileInputRef}
                type="file"
                className="d-none"
                onChange={(e) => {
                  const input = e.currentTarget as HTMLInputElement
                  const file = input.files?.[0] || null
                  setNewVersionFile(file)
                  setNewVersionFileName(file?.name || '')
                }}
              />
              <div className="input-group input-group-sm">
                <Button variant="outline-secondary" type="button" onClick={() => newVersionFileInputRef.current?.click()}>
                  {t('choose_file')}
                </Button>
                <Form.Control type="text" size="sm" readOnly value={newVersionFileName || t('no_file_chosen')} />
              </div>
            </Form.Group>
            <Form.Group>
              <Form.Label>{t('comment')}</Form.Label>
              <Form.Control as="textarea" rows={3} value={newVersionComment} onChange={(e) => setNewVersionComment(e.target.value)} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowNewVersionDialog(false)} disabled={creatingVersion}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={createNewVersion} disabled={creatingVersion || !newVersionFile}>
            {creatingVersion && <Spinner animation="border" size="sm" className="me-1" />}
            {t('create')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Row>
        {/* Left column - Form */}
        <Col md={6} xl={5} className="mb-4">
          <div className="btn-toolbar mb-1 border-bottom">
            <div className="btn-group pb-3 rounded-btn-group">
              <Button variant="outline-secondary" size="sm" onClick={close} title={t('close')}>
                <i className="bi bi-x" style={{ width: '1.2em', height: '1.2em' }}></i>
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={previousDoc} disabled={!hasPrevious()} title={t('previous')}>
                <i className="bi bi-arrow-left" style={{ width: '1.2em', height: '1.2em' }}></i>
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={nextDoc} disabled={!hasNext()} title={t('next')}>
                <i className="bi bi-arrow-right" style={{ width: '1.2em', height: '1.2em' }}></i>
              </Button>
            </div>

            <div className="btn-group pb-3 ms-auto rounded-btn-group">
              <Button variant="outline-secondary" size="sm" onClick={discard} disabled={!isDirty || saving}>
                {t('discard')}
              </Button>
              <Button variant="primary" size="sm" onClick={() => save(false)} disabled={!userCanEdit || !isDirty || saving}>
                {saving && <Spinner animation="border" size="sm" className="me-1" />}
                {t('save')}
              </Button>
              <Button variant="outline-primary" size="sm" onClick={() => save(true)} disabled={!userCanEdit || !isDirty || saving}>
                {t('save_and_close')}
              </Button>
            </div>
          </div>

          <Tab.Container
            activeKey={activeTab}
            onSelect={(k) => {
              if (k && k !== activeTab) {
                // Navigate to new section URL (like React)
                // Use replace: true to avoid adding to history for smoother tab switching
                navigate(`/documents/${documentId}/${k}`, { replace: true })
              }
            }}
          >
            <Nav variant="underline" className="flex-nowrap flex-md-wrap overflow-auto mb-3">
              <Nav.Item>
                <Nav.Link eventKey={DocumentDetailNavIDs.Details}>{t('details')}</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey={DocumentDetailNavIDs.Content}>{t('content')}</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey={DocumentDetailNavIDs.Metadata}>{t('metadata')}</Nav.Link>
              </Nav.Item>
              {canViewNotes && (
                <Nav.Item>
                  <Nav.Link eventKey={DocumentDetailNavIDs.Notes} className="d-flex align-items-center">
                    <span>{t('notes')}</span>
                    {notes.length > 0 && <span className="badge ms-auto">{notes.length}</span>}
                  </Nav.Link>
                </Nav.Item>
              )}
              <Nav.Item>
                <Nav.Link eventKey={DocumentDetailNavIDs.Versions}>{t('versions')}</Nav.Link>
              </Nav.Item>
              {canViewHistory && (
                <Nav.Item>
                  <Nav.Link eventKey={DocumentDetailNavIDs.History}>{t('history')}</Nav.Link>
                </Nav.Item>
              )}
              <Nav.Item>
                <Nav.Link eventKey={DocumentDetailNavIDs.Permissions}>{t('permissions')}</Nav.Link>
              </Nav.Item>
              <Nav.Item className="ms-auto">
                <Button
                  variant="link"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault()
                    setShowTabsDocsDialog(true)
                  }}
                  title={t('help')}
                  className="p-0 text-muted"
                  style={{ width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <i className="bi bi-info-circle"></i>
                </Button>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              {/* DETAILS TAB */}
              <Tab.Pane eventKey={DocumentDetailNavIDs.Details}>
                <Form onSubmit={(e) => e.preventDefault()}>
                  {/* Title */}
                  {workflowCanViewSystemField('title') && <Form.Group as={Row} className={`mb-3 align-items-center ${workflowRequiredFieldErrors.systemFields.has('title') ? 'workflow-required-field-error' : ''}`}>
                    <Form.Label column sm={3} className="fw-medium">
                      {t('title')}
                      {workflowSystemFieldRequired('title') && <span className="text-danger ms-1">*</span>}
                    </Form.Label>
                    <Col sm={9}>
                      <Form.Control
                        type="text"
                        value={formData.title || ''}
                        onChange={(e) => handleFieldChange('title', e.target.value)}
                        size="sm"
                        disabled={!userCanEdit || !workflowCanEditSystemField('title')}
                      />
                      <div className="text-muted small mt-1">
                        {t('current_version')}: V{document.version_number}
                        {document.workflow_status && (
                          <>
                            {' · '}
                            {t('current_status')}: {document.workflow_status.name}
                          </>
                        )}
                      </div>
                      {workflowRequiredFieldErrors.systemFields.has('title') && (
                        <Form.Text className="text-danger">{t('document_workflow.errors.required_attribute')}</Form.Text>
                      )}
                    </Col>
                  </Form.Group>}

                  {/* ASN */}
                  {workflowCanViewSystemField('archive_serial_number') && <Form.Group as={Row} className={`mb-3 align-items-center ${workflowRequiredFieldErrors.systemFields.has('archive_serial_number') ? 'workflow-required-field-error' : ''}`}>
                    <Form.Label column sm={3} className="fw-medium">
                      {t('asn')}
                      {workflowSystemFieldRequired('archive_serial_number') && <span className="text-danger ms-1">*</span>}
                    </Form.Label>
                    <Col sm={9}>
                      <div className="input-group input-group-sm">
                        <Form.Control
                          type="number"
                          value={formData.archive_serial_number || ''}
                          onChange={(e) => {
                            const val = parseInt(e.target.value)
                            handleFieldChange('archive_serial_number', !isNaN(val) ? val : null)
                          }}
                          size="sm"
                          disabled={!userCanEdit || !workflowCanEditSystemField('archive_serial_number')}
                        />
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={getNextAsn}
                          disabled={!userCanEdit || !workflowCanEditSystemField('archive_serial_number') || !!formData.archive_serial_number}
                          title={t('get_next_asn')}
                        >
                          +1
                        </Button>
                      </div>
                      {workflowRequiredFieldErrors.systemFields.has('archive_serial_number') && (
                        <Form.Text className="text-danger">{t('document_workflow.errors.required_attribute')}</Form.Text>
                      )}
                    </Col>
                  </Form.Group>}

                  {/* Created Date */}
                  {workflowCanViewSystemField('created') && <Form.Group as={Row} className={`mb-3 align-items-center ${workflowRequiredFieldErrors.systemFields.has('created') ? 'workflow-required-field-error' : ''}`}>
                    <Form.Label column sm={3} className="fw-medium">
                      {t('created')}
                      {workflowSystemFieldRequired('created') && <span className="text-danger ms-1">*</span>}
                    </Form.Label>
                    <Col sm={9}>
                      <div className="input-group input-group-sm">
                        {editingCreatedDate ? (
                          <Form.Control
                            type="date"
                            value={formData.created ? formData.created.split('T')[0] : ''}
                            onChange={(e) => {
                              handleFieldChange('created', e.target.value)
                              setEditingCreatedDate(false)
                            }}
                            onBlur={() => setEditingCreatedDate(false)}
                            size="sm"
                            autoFocus
                            disabled={!userCanEdit || !workflowCanEditSystemField('created')}
                          />
                        ) : (
                          <Form.Control
                            type="text"
                            readOnly
                            value={formData.created ? formatDate(formData.created) : ''}
                            onClick={() => {
                              if (userCanEdit && workflowCanEditSystemField('created')) setEditingCreatedDate(true)
                            }}
                            size="sm"
                            style={{ cursor: 'pointer' }}
                          />
                        )}
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setEditingCreatedDate(!editingCreatedDate)}
                          disabled={!userCanEdit || !workflowCanEditSystemField('created')}
                          title={editingCreatedDate ? t('close') : t('edit')}
                        >
                          <i className={editingCreatedDate ? 'bi bi-check' : 'bi bi-calendar'}></i>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            const createdDate = formData.created ? formData.created.split('T')[0] : null
                            if (createdDate) {
                              filterByDate(createdDate)
                            }
                          }}
                          disabled={!formData.created}
                          title={t('show_filter')}
                        >
                          <i className="bi bi-funnel"></i>
                        </Button>
                      </div>
                      {userCanEdit && workflowCanEditSystemField('created') && suggestions?.dates &&
                        suggestions.dates.length > 0 &&
                        (() => {
                          const currentDate = formData.created ? formData.created.split('T')[0] : null
                          const filteredDates = suggestions.dates.filter((d: string) => d !== currentDate)
                          return filteredDates.length > 0 ? (
                            <Form.Text>
                              {t('suggestions')}:{' '}
                              {filteredDates.map((dateStr: string, index: number) => (
                                <span key={dateStr}>
                                  <a
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      handleFieldChange('created', dateStr)
                                    }}
                                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                  >
                                    {formatDate(dateStr)}
                                  </a>
                                  {index < filteredDates.length - 1 && ', '}
                                </span>
                              ))}
                            </Form.Text>
                          ) : null
                        })()}
                      {workflowRequiredFieldErrors.systemFields.has('created') && (
                        <Form.Text className="text-danger">{t('document_workflow.errors.required_attribute')}</Form.Text>
                      )}
                    </Col>
                  </Form.Group>}

                  {/* Correspondent */}
                  {workflowCanViewSystemField('correspondent') && <Form.Group as={Row} className={`mb-3 align-items-center ${workflowRequiredFieldErrors.systemFields.has('correspondent') ? 'workflow-required-field-error' : ''}`}>
                    <Form.Label column sm={3} className="fw-medium">
                      {t('correspondent')}
                      {workflowSystemFieldRequired('correspondent') && <span className="text-danger ms-1">*</span>}
                    </Form.Label>
                    <Col sm={9}>
                      <div className="input-group input-group-sm">
                        <Form.Select
                          value={typeof formData.correspondent === 'object' ? formData.correspondent?.id : formData.correspondent || ''}
                          onChange={(e) => {
                            const val = parseInt(e.target.value)
                            handleFieldChange('correspondent', !isNaN(val) ? val : null)
                          }}
                          size="sm"
                          disabled={!userCanEdit || !workflowCanEditSystemField('correspondent')}
                        >
                          <option value="">{t('not_assigned')}</option>
                          {correspondentOptions.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </Form.Select>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleFieldChange('correspondent', null)}
                          title={t('clear')}
                          disabled={!userCanEdit || !workflowCanEditSystemField('correspondent')}
                        >
                          <i className="bi bi-x"></i>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={openCreateCorrespondent}
                          title={t('create_new_correspondent')}
                          disabled={!userCanEdit || !workflowCanEditSystemField('correspondent')}
                        >
                          <i className="bi bi-plus"></i>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            const correspondentId = typeof formData.correspondent === 'object' ? formData.correspondent?.id : formData.correspondent
                            filterDocuments(correspondentId, FILTER_CORRESPONDENT)
                          }}
                          disabled={!formData.correspondent}
                          title={t('show_filter')}
                        >
                          <i className="bi bi-funnel"></i>
                        </Button>
                      </div>
                      {userCanEdit && workflowCanEditSystemField('correspondent') && suggestions?.correspondents &&
                        suggestions.correspondents.length > 0 &&
                        (() => {
                          const currentId = typeof formData.correspondent === 'object' ? formData.correspondent?.id : formData.correspondent
                          const filteredIds = suggestions.correspondents.filter((id: number) => id !== currentId)
                          return filteredIds.length > 0 ? (
                            <Form.Text>
                              {t('suggestions')}:{' '}
                              {filteredIds.map((id: number, index: number) => {
                                const correspondent = correspondents.find((c) => c.id === id)
                                return correspondent ? (
                                  <span key={id}>
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        handleFieldChange('correspondent', id)
                                      }}
                                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                    >
                                      {correspondent.name}
                                    </a>
                                    {index < filteredIds.length - 1 && ', '}
                                  </span>
                                ) : null
                              })}
                            </Form.Text>
                          ) : null
                        })()}
                      {workflowRequiredFieldErrors.systemFields.has('correspondent') && (
                        <Form.Text className="text-danger">{t('document_workflow.errors.required_attribute')}</Form.Text>
                      )}
                    </Col>
                  </Form.Group>}

                  {/* Document Type */}
                  {workflowCanViewSystemField('document_type') && <Form.Group as={Row} className={`mb-3 align-items-center ${workflowRequiredFieldErrors.systemFields.has('document_type') ? 'workflow-required-field-error' : ''}`}>
                    <Form.Label column sm={3} className="fw-medium">
                      {t('document_type')}
                      {workflowSystemFieldRequired('document_type') && <span className="text-danger ms-1">*</span>}
                    </Form.Label>
                    <Col sm={9}>
                      <div className="input-group input-group-sm">
                        <Form.Select
                          value={typeof formData.document_type === 'object' ? formData.document_type?.id : formData.document_type || ''}
                          onChange={(e) => {
                            const val = parseInt(e.target.value)
                            handleFieldChange('document_type', !isNaN(val) ? val : null)
                          }}
                          size="sm"
                          disabled={!userCanEdit || !workflowCanEditSystemField('document_type')}
                        >
                          <option value="">{t('not_assigned')}</option>
                          {documentTypeOptions.map((dt) => (
                            <option key={dt.id} value={dt.id}>
                              {dt.name}
                            </option>
                          ))}
                        </Form.Select>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleFieldChange('document_type', null)}
                          title={t('clear')}
                          disabled={!userCanEdit || !workflowCanEditSystemField('document_type')}
                        >
                          <i className="bi bi-x"></i>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={openCreateDocumentType}
                          title={t('create_new_document_type')}
                          disabled={!userCanEdit || !workflowCanEditSystemField('document_type')}
                        >
                          <i className="bi bi-plus"></i>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            const documentTypeId = typeof formData.document_type === 'object' ? formData.document_type?.id : formData.document_type
                            filterDocuments(documentTypeId, FILTER_DOCUMENT_TYPE)
                          }}
                          disabled={!formData.document_type}
                          title={t('show_filter')}
                        >
                          <i className="bi bi-funnel"></i>
                        </Button>
                      </div>
                      {userCanEdit && workflowCanEditSystemField('document_type') && suggestions?.document_types &&
                        suggestions.document_types.length > 0 &&
                        (() => {
                          const currentId = typeof formData.document_type === 'object' ? formData.document_type?.id : formData.document_type
                          const filteredIds = suggestions.document_types.filter((id: number) => id !== currentId)
                          return filteredIds.length > 0 ? (
                            <Form.Text>
                              {t('suggestions')}:{' '}
                              {filteredIds.map((id: number, index: number) => {
                                const docType = documentTypes.find((dt) => dt.id === id)
                                return docType ? (
                                  <span key={id}>
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        handleFieldChange('document_type', id)
                                      }}
                                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                    >
                                      {docType.name}
                                    </a>
                                    {index < filteredIds.length - 1 && ', '}
                                  </span>
                                ) : null
                              })}
                            </Form.Text>
                          ) : null
                        })()}
                      {workflowRequiredFieldErrors.systemFields.has('document_type') && (
                        <Form.Text className="text-danger">{t('document_workflow.errors.required_attribute')}</Form.Text>
                      )}
                    </Col>
                  </Form.Group>}

                  {/* Storage Path */}
                  {workflowCanViewSystemField('storage_path') && <Form.Group as={Row} className={`mb-3 align-items-center ${workflowRequiredFieldErrors.systemFields.has('storage_path') ? 'workflow-required-field-error' : ''}`}>
                    <Form.Label column sm={3} className="fw-medium">
                      {t('storage_path')}
                      {workflowSystemFieldRequired('storage_path') && <span className="text-danger ms-1">*</span>}
                    </Form.Label>
                    <Col sm={9}>
                      <div className="input-group input-group-sm">
                        <Form.Select
                          value={typeof formData.storage_path === 'object' ? formData.storage_path?.id : formData.storage_path || ''}
                          onChange={(e) => {
                            const val = parseInt(e.target.value)
                            handleFieldChange('storage_path', !isNaN(val) ? val : null)
                          }}
                          size="sm"
                          disabled={!userCanEdit || !workflowCanEditSystemField('storage_path')}
                        >
                          <option value="">{t('default')}</option>
                          {storagePathOptions.map((sp) => (
                            <option key={sp.id} value={sp.id}>
                              {sp.name}
                            </option>
                          ))}
                        </Form.Select>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleFieldChange('storage_path', null)}
                          title={t('clear')}
                          disabled={!userCanEdit || !workflowCanEditSystemField('storage_path')}
                        >
                          <i className="bi bi-x"></i>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={openCreateStoragePath}
                          title={t('create_new_storage_path')}
                          disabled={!userCanEdit || !workflowCanEditSystemField('storage_path')}
                        >
                          <i className="bi bi-plus"></i>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            const storagePathId = typeof formData.storage_path === 'object' ? formData.storage_path?.id : formData.storage_path
                            filterDocuments(storagePathId, FILTER_STORAGE_PATH)
                          }}
                          disabled={!formData.storage_path}
                          title={t('show_filter')}
                        >
                          <i className="bi bi-funnel"></i>
                        </Button>
                      </div>
                      {userCanEdit && workflowCanEditSystemField('storage_path') && suggestions?.storage_paths &&
                        suggestions.storage_paths.length > 0 &&
                        (() => {
                          const currentId = typeof formData.storage_path === 'object' ? formData.storage_path?.id : formData.storage_path
                          const filteredIds = suggestions.storage_paths.filter((id: number) => id !== currentId)
                          return filteredIds.length > 0 ? (
                            <Form.Text>
                              {t('suggestions')}:{' '}
                              {filteredIds.map((id: number, index: number) => {
                                const storagePath = storagePaths.find((sp) => sp.id === id)
                                return storagePath ? (
                                  <span key={id}>
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        handleFieldChange('storage_path', id)
                                      }}
                                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                    >
                                      {storagePath.name}
                                    </a>
                                    {index < filteredIds.length - 1 && ', '}
                                  </span>
                                ) : null
                              })}
                            </Form.Text>
                          ) : null
                        })()}
                      {workflowRequiredFieldErrors.systemFields.has('storage_path') && (
                        <Form.Text className="text-danger">{t('document_workflow.errors.required_attribute')}</Form.Text>
                      )}
                    </Col>
                  </Form.Group>}

                  {/* Tags */}
                  {workflowCanViewSystemField('tags') && <Form.Group as={Row} className={`mb-3 align-items-center ${workflowRequiredFieldErrors.systemFields.has('tags') ? 'workflow-required-field-error' : ''}`}>
                    <Form.Label column sm={3} className="fw-medium">
                      {t('tags')}
                      {workflowSystemFieldRequired('tags') && <span className="text-danger ms-1">*</span>}
                    </Form.Label>
                    <Col sm={9}>
                      <InputTags
                        value={formData.tags || []}
                        onChange={(newTags) => handleFieldChange('tags', newTags)}
                        disabled={!userCanEdit || !workflowCanEditSystemField('tags')}
                        options={tagOptionsFallback}
                        horizontal={true}
                        showFilter={true}
                        onFilterDocuments={(tags) => filterDocuments(tags, FILTER_HAS_TAGS_ALL)}
                        onCreateNew={openCreateTag}
                        hideAddButton={!permissionsService.currentUserCan(PermissionAction.Add, PermissionType.Tag) || !workflowCanEditSystemField('tags')}
                        suggestions={suggestions?.tags}
                      />
                      {workflowRequiredFieldErrors.systemFields.has('tags') && (
                        <Form.Text className="text-danger">{t('document_workflow.errors.required_attribute')}</Form.Text>
                      )}
                    </Col>
                  </Form.Group>}

                  {/* Custom Fields */}
                  {document?.custom_fields && document.custom_fields.length > 0 && (
                    <div className="custom-fields-panel document-detail-section-divider">
                      <h6 className="fw-bold mb-3 text-muted">{t('document_detail_additional_attributes')}</h6>
                      {(() => {
                        const configs = (activeDocumentType?.custom_field_configs || []) as any[]
                        const separatorItems = configs
                          .filter((cfg) => cfg.custom_field_data_type === 'separator')
                          .map((cfg) => ({
                            kind: 'separator' as const,
                            order: cfg.order ?? Number.MAX_SAFE_INTEGER,
                            cfg,
                          }))
                        const instanceItems = [...document.custom_fields]
                          .filter((instance) => !hiddenCustomFieldIds.has(instance.field))
                          .map((instance) => {
                            const f = getCustomFieldFromInstance(instance)
                            const order = f ? getCustomFieldMeta(f).order : Number.MAX_SAFE_INTEGER
                            return { kind: 'instance' as const, order, instance }
                          })
                        const presentFieldIds = new Set(document.custom_fields.map((cf) => cf.field))
                        const syntheticItems = configs
                          .filter(
                            (cfg) =>
                              cfg.visibility_query != null &&
                              cfg.custom_field_data_type !== 'separator' &&
                              !presentFieldIds.has(cfg.custom_field) &&
                              !hiddenCustomFieldIds.has(cfg.custom_field)
                          )
                          .map((cfg) => ({
                            kind: 'instance' as const,
                            order: cfg.order ?? Number.MAX_SAFE_INTEGER,
                            instance: { field: cfg.custom_field, value: null } as CustomFieldInstance,
                          }))
                        const merged = [...instanceItems, ...syntheticItems, ...separatorItems].sort((a, b) => a.order - b.order)
                        return merged.map((item, idx) => {
                          if (item.kind === 'separator') {
                            const hasAttributeBefore = merged[idx - 1]?.kind === 'instance'
                            const hasAttributeAfter = merged[idx + 1]?.kind === 'instance'
                            if (!hasAttributeBefore || !hasAttributeAfter) {
                              return null
                            }
                            const label = item.cfg.override_name || item.cfg.custom_field_name || ''
                            return (
                              <div
                                key={`sep-${item.cfg.custom_field}-${idx}`}
                                className="attribute-separator"
                                role="separator"
                                aria-label={label || t('separator')}
                              />
                            )
                          }
                          const fieldInstance = item.instance
                          const field = getCustomFieldFromInstance(fieldInstance)
                          if (!field) {
                            const fallbackValue =
                              fieldInstance.value == null
                                ? ''
                                : typeof fieldInstance.value === 'object'
                                  ? JSON.stringify(fieldInstance.value)
                                  : String(fieldInstance.value)
                            return (
                              <Form.Group key={fieldInstance.field} as={Row} className="mb-3 align-items-center">
                                <Form.Label column sm={3} className="fw-medium">
                                  {`${t('custom_field')} #${fieldInstance.field}`}
                                </Form.Label>
                                <Col sm={9}>
                                  <Form.Control value={fallbackValue} readOnly size="sm" disabled />
                                </Col>
                              </Form.Group>
                            )
                          }
                          const fieldMeta = getCustomFieldMeta(field)
                          const fieldCanEdit = userCanEdit && workflowCanEditField(fieldInstance.field)
                          const fieldHasWorkflowError = workflowRequiredFieldErrors.customFields.has(fieldInstance.field)

                          return (
                            <Form.Group
                              key={fieldInstance.field}
                              as={Row}
                              className={`mb-3 align-items-center ${fieldHasWorkflowError ? 'workflow-required-field-error' : ''}`}
                            >
                              <Form.Label column sm={3} className="fw-medium">
                                {fieldMeta.label}
                                {fieldMeta.required && <span className="text-danger ms-1">*</span>}
                              </Form.Label>
                              <Col sm={9}>
                                {field.data_type === 'string' && (
                                  <div className="input-group input-group-sm" style={{ width: '100%' }}>
                                    <Form.Control
                                      type="text"
                                      value={fieldInstance.value || ''}
                                      onChange={(e) => handleCustomFieldValueChange(fieldInstance.field, e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }
                                      }}
                                      size="sm"
                                      placeholder={fieldMeta.placeholder}
                                      disabled={!fieldCanEdit}
                                    />
                                    {fieldCanEdit && (
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleRemoveCustomField(fieldInstance)}
                                        title={t('remove')}
                                      >
                                        <i className="bi bi-x"></i>
                                      </Button>
                                    )}
                                  </div>
                                )}
                                {field.data_type === 'date' && (
                                  <div className="input-group input-group-sm" style={{ width: '100%' }}>
                                    <Form.Control
                                      type="date"
                                      value={fieldInstance.value || ''}
                                      onChange={(e) => handleCustomFieldValueChange(fieldInstance.field, e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }
                                      }}
                                      size="sm"
                                      placeholder={fieldMeta.placeholder}
                                      disabled={!fieldCanEdit}
                                    />
                                    {fieldCanEdit && (
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleRemoveCustomField(fieldInstance)}
                                        title={t('remove')}
                                      >
                                        <i className="bi bi-x"></i>
                                      </Button>
                                    )}
                                  </div>
                                )}
                                {field.data_type === 'integer' && (
                                  <div className="input-group input-group-sm" style={{ width: '100%' }}>
                                    <Form.Control
                                      type="number"
                                      value={fieldInstance.value || ''}
                                      onChange={(e) =>
                                        handleCustomFieldValueChange(fieldInstance.field, e.target.value === '' ? null : parseInt(e.target.value, 10))
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }
                                      }}
                                      size="sm"
                                      placeholder={fieldMeta.placeholder}
                                      disabled={!fieldCanEdit}
                                    />
                                    {fieldCanEdit && (
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleRemoveCustomField(fieldInstance)}
                                        title={t('remove')}
                                      >
                                        <i className="bi bi-x"></i>
                                      </Button>
                                    )}
                                  </div>
                                )}
                                {field.data_type === 'float' && (
                                  <div className="input-group input-group-sm" style={{ width: '100%' }}>
                                    <Form.Control
                                      type="number"
                                      step="0.1"
                                      value={fieldInstance.value || ''}
                                      onChange={(e) =>
                                        handleCustomFieldValueChange(fieldInstance.field, e.target.value === '' ? null : parseFloat(e.target.value))
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }
                                      }}
                                      size="sm"
                                      placeholder={fieldMeta.placeholder}
                                      disabled={!fieldCanEdit}
                                    />
                                    {fieldCanEdit && (
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleRemoveCustomField(fieldInstance)}
                                        title={t('remove')}
                                      >
                                        <i className="bi bi-x"></i>
                                      </Button>
                                    )}
                                  </div>
                                )}
                                {field.data_type === 'boolean' && (
                                  <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                                    <Form.Check
                                      type="checkbox"
                                      checked={fieldInstance.value || false}
                                      onChange={(e) => handleCustomFieldValueChange(fieldInstance.field, e.target.checked)}
                                      disabled={!fieldCanEdit}
                                    />
                                    {fieldCanEdit && (
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleRemoveCustomField(fieldInstance)}
                                        title={t('remove')}
                                      >
                                        <i className="bi bi-x"></i>
                                      </Button>
                                    )}
                                  </div>
                                )}
                                {field.data_type === 'url' && (
                                  <div className="input-group input-group-sm" style={{ width: '100%' }}>
                                    <Form.Control
                                      type="url"
                                      value={fieldInstance.value || ''}
                                      onChange={(e) => handleCustomFieldValueChange(fieldInstance.field, e.target.value)}
                                      placeholder={fieldMeta.placeholder || 'https://'}
                                      size="sm"
                                      disabled={!fieldCanEdit}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }
                                      }}
                                    />
                                    <button
                                      className="btn btn-outline-secondary"
                                      type="button"
                                      onClick={() => {
                                        if (fieldInstance.value) {
                                          window.open(fieldInstance.value, '_blank', 'noopener,noreferrer')
                                        }
                                      }}
                                      disabled={!fieldCanEdit || !fieldInstance.value}
                                      title={!fieldCanEdit || !fieldInstance.value ? '' : 'Open link'}
                                    >
                                      <i className="bi bi-box-arrow-up-right" style={{ width: '1.2em', height: '1.2em' }}></i>
                                    </button>
                                    {fieldCanEdit && (
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleRemoveCustomField(fieldInstance)}
                                        title={t('remove')}
                                      >
                                        <i className="bi bi-x"></i>
                                      </Button>
                                    )}
                                  </div>
                                )}
                                {field.data_type === 'monetary' && (
                                  <div className="d-flex align-items-center gap-1" style={{ width: '100%' }}>
                                    <MonetaryInput
                                      value={fieldInstance.value || ''}
                                      onChange={(v) => handleCustomFieldValueChange(fieldInstance.field, v)}
                                      defaultCurrency={field.extra_data?.default_currency || 'RSD'}
                                      size="sm"
                                      disabled={!fieldCanEdit}
                                    />
                                    {fieldCanEdit && (
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleRemoveCustomField(fieldInstance)}
                                        title={t('remove')}
                                      >
                                        <i className="bi bi-x"></i>
                                      </Button>
                                    )}
                                  </div>
                                )}
                                {field.data_type === 'counter' && (
                                  <div className="input-group input-group-sm" style={{ width: '100%' }}>
                                    <Form.Control
                                      type="text"
                                      value={fieldInstance.value || ''}
                                      readOnly
                                      disabled
                                      size="sm"
                                      placeholder={
                                        fieldInstance.counter_info?.missing_variables?.length
                                          ? t('counter_placeholder', {
                                              variables: fieldInstance.counter_info.missing_variables.join(', '),
                                            })
                                          : t('counter_pending')
                                      }
                                    />
                                    <span className="input-group-text" title={t('counter_frozen_help')}>
                                      <i className="bi bi-lock"></i>
                                    </span>
                                  </div>
                                )}
                                {field.data_type === 'select' && (
                                  <div className="input-group input-group-sm" style={{ width: '100%' }}>
                                    <Form.Select
                                      value={fieldInstance.value || ''}
                                      onChange={(e) =>
                                        handleCustomFieldValueChange(fieldInstance.field, e.target.value === '' ? null : e.target.value)
                                      }
                                      disabled={!fieldCanEdit}
                                      size="sm"
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }
                                      }}
                                    >
                                      <option value="">{t('select_option')}</option>
                                      {(field.extra_data?.select_options || []).map((option) => (
                                        <option key={option.id} value={option.id}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </Form.Select>
                                    {fieldCanEdit && (
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleRemoveCustomField(fieldInstance)}
                                        title={t('remove')}
                                      >
                                        <i className="bi bi-x"></i>
                                      </Button>
                                    )}
                                  </div>
                                )}
                                {field.data_type === 'documentlink' && (
                                  <div className="d-flex align-items-start" style={{ width: '100%' }}>
                                    <div className="flex-grow-1">
                                      <DocumentLinkInput
                                        value={Array.isArray(fieldInstance.value) ? fieldInstance.value : []}
                                        onChange={(value) => handleCustomFieldValueChange(fieldInstance.field, value)}
                                        disabled={!fieldCanEdit}
                                        parentDocumentId={documentId}
                                      />
                                    </div>
                                    {fieldCanEdit && (
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleRemoveCustomField(fieldInstance)}
                                        title={t('remove')}
                                        className="ms-2"
                                      >
                                        <i className="bi bi-x"></i>
                                      </Button>
                                    )}
                                  </div>
                                )}
                                {field.data_type === 'longtext' && (
                                  <div className="input-group input-group-sm" style={{ width: '100%' }}>
                                    <Form.Control
                                      as="textarea"
                                      rows={3}
                                      value={fieldInstance.value || ''}
                                      onChange={(e) => handleCustomFieldValueChange(fieldInstance.field, e.target.value)}
                                      onKeyDown={(e) => {
                                        // Za textarea, Enter je normalan karakter, ali Ctrl+Enter neće submitovati
                                        if (e.key === 'Enter' && e.ctrlKey) {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }
                                      }}
                                      size="sm"
                                      placeholder={fieldMeta.placeholder}
                                      disabled={!fieldCanEdit}
                                    />
                                    {fieldCanEdit && (
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleRemoveCustomField(fieldInstance)}
                                        title={t('remove')}
                                      >
                                        <i className="bi bi-x"></i>
                                      </Button>
                                    )}
                                  </div>
                                )}
                                {fieldHasWorkflowError && (
                                  <Form.Text className="text-danger">{t('document_workflow.errors.required_attribute')}</Form.Text>
                                )}
                              </Col>
                            </Form.Group>
                          )
                        })
                      })()}
                    </div>
                  )}

                  <div className="custom-fields-panel document-detail-section-divider">
                    <div className="d-flex align-items-center justify-content-between gap-2 mb-3 flex-wrap w-100">
                      <h6 className="fw-bold mb-0 text-muted">{t('document_workflow.sidebar_workflow')}</h6>
                      {canOpenDocumentWorkflowPopup && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="flex-shrink-0"
                          onClick={openDocumentWorkflowPopup}
                          title={t('document_workflow.title')}
                        >
                          <i className="bi bi-diagram-3" />
                          <span className="ms-1">{t('document_workflow.start_workflow_shortcut')}</span>
                        </Button>
                      )}
                    </div>
                    <DocumentDetailWorkflow
                      documentId={document?.id ?? 0}
                      documentWorkflowStatus={(() => {
                        const fd = formData.workflow_status
                        if (fd != null && typeof fd === 'object' && fd !== null && 'name' in fd) {
                          return fd as WorkflowStatus
                        }
                        return document?.workflow_status ?? null
                      })()}
                      workflowAvailableActions={workflowAvailableActions}
                      workflowStatuses={workflowStatuses}
                      workflowHistory={workflowHistory}
                      onWorkflowAction={handleStepperWorkflowAction}
                      workflowStatusId={(() => {
                        const ws = formData.workflow_status
                        if (ws != null) {
                          const id = typeof ws === 'object' && ws !== null && 'id' in ws ? (ws as { id: number }).id : ws
                          if (typeof id === 'number' && !Number.isNaN(id)) return id
                        }
                        const docWs = document?.workflow_status
                        if (docWs != null) {
                          const id = typeof docWs === 'object' && docWs !== null && 'id' in docWs ? (docWs as { id: number }).id : docWs
                          if (typeof id === 'number' && !Number.isNaN(id)) return id
                        }
                        return workflowAvailableActions?.status_id ?? null
                      })()}
                      showStepperModal={showWorkflowStepperModal}
                      setShowStepperModal={setShowWorkflowStepperModal}
                      onClaim={handleClaim}
                      onUnclaim={handleUnclaim}
                      claimedById={claimedById}
                      isClaimedByMe={isClaimedByMe}
                      claimSubmitting={claimSubmitting}
                      userCanEdit={userCanEdit}
                      claimedByUsername={claimedByUsername}
                    />
                  </div>

                  <DocumentWorkflowStartChoiceModal
                    show={showStartWorkflowChoiceModal}
                    onHide={() => setShowStartWorkflowChoiceModal(false)}
                    availableDefinitions={workflowAvailableActions?.available_definitions ?? []}
                    onStart={handleStartWorkflowDefinition}
                    emptyMessage={document?.document_type == null ? t('document_workflow.no_document_type') : undefined}
                  />
                </Form>

                {document && <RelatedItemsSection documentId={documentId} canEdit={userCanEdit} />}

                {/* Save buttons at bottom */}
                <div className="d-flex border-top pt-3 mt-4">
                  <div className="btn-group ms-auto rounded-btn-group">
                    <Button variant="outline-secondary" size="sm" onClick={discard} disabled={!isDirty || saving}>
                      {t('discard')}
                    </Button>
                    <Button variant="outline-primary" size="sm" onClick={() => save(true)} disabled={!userCanEdit || !isDirty || saving}>
                      {t('save_and_next')}
                    </Button>
                    <Button variant="primary" size="sm" onClick={() => save(false)} disabled={!userCanEdit || !isDirty || saving}>
                      {saving && <Spinner animation="border" size="sm" className="me-1" />}
                      {t('save')}
                    </Button>
                  </div>
                </div>
              </Tab.Pane>

              {/* CONTENT TAB */}
              <Tab.Pane eventKey={DocumentDetailNavIDs.Content}>
                <Form.Control
                  as="textarea"
                  readOnly
                  rows={20}
                  value={formData.content || ''}
                  className="font-monospace"
                  style={{ whiteSpace: 'pre-wrap' }}
                />
              </Tab.Pane>

              {/* METADATA TAB */}
              <Tab.Pane eventKey={DocumentDetailNavIDs.Metadata}>
                <Table borderless size="sm">
                  <tbody>
                    <tr>
                      <td>{t('date_modified')}</td>
                      <td>{document.modified && formatDate(document.modified)}</td>
                    </tr>
                    <tr>
                      <td>{t('date_added')}</td>
                      <td>{document.added && formatDate(document.added)}</td>
                    </tr>
                    <tr>
                      <td>{t('media_filename')}</td>
                      <td>{metadata?.media_filename}</td>
                    </tr>
                    <tr>
                      <td>{t('original_filename')}</td>
                      <td>{metadata?.original_filename}</td>
                    </tr>
                    <tr>
                      <td>{t('original_md5_checksum')}</td>
                      <td>{metadata?.original_checksum}</td>
                    </tr>
                    <tr>
                      <td>{t('original_file_size')}</td>
                      <td>{metadata?.original_size && formatBytes(metadata.original_size)}</td>
                    </tr>
                    <tr>
                      <td>{t('original_mime_type')}</td>
                      <td>{metadata?.original_mime_type}</td>
                    </tr>
                    {metadata?.has_archive_version && (
                      <>
                        <tr>
                          <td>{t('archive_md5_checksum')}</td>
                          <td>{metadata?.archive_checksum}</td>
                        </tr>
                        <tr>
                          <td>{t('archive_file_size')}</td>
                          <td>{metadata?.archive_size && formatBytes(metadata.archive_size)}</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </Table>
              </Tab.Pane>

              {/* NOTES TAB */}
              {canViewNotes && (
                <Tab.Pane eventKey={DocumentDetailNavIDs.Notes}>
                  <DocumentNotes
                    documentId={documentId}
                    addDisabled={!userCanAddNote}
                    deleteDisabled={!userCanDeleteNote}
                    editDisabled={!userCanEditNoteContent}
                  />
                </Tab.Pane>
              )}

              {/* VERSIONS TAB */}
              <Tab.Pane eventKey={DocumentDetailNavIDs.Versions}>
                <DocumentVersions
                  documentId={documentId}
                  refreshKey={versionsRefreshKey}
                  onRestored={async (restoredVersion) => {
                    await invalidateDocumentQueries()
                    if (restoredVersion?.id && restoredVersion.id !== documentId) {
                      navigate(`/documents/${restoredVersion.id}/${DocumentDetailNavIDs.Versions}`, { replace: true })
                      return
                    }
                    await loadDocument(true)
                    await refreshWorkflowPanelOnly(documentId)
                  }}
                />
              </Tab.Pane>

              {/* HISTORY TAB (podtabovi: All, Workflow, eSign) */}
              {canViewHistory && (
                <Tab.Pane eventKey={DocumentDetailNavIDs.History}>
                  <Tab.Container activeKey={effectiveHistorySubTab} onSelect={(k) => k && setHistorySubTab(k as 'all' | 'workflow' | 'esign')}>
                    <Nav variant="underline" className="mb-3">
                      <Nav.Item>
                        <Nav.Link eventKey="all">{t('all')}</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="workflow">{t('document_workflow.history.title')}</Nav.Link>
                      </Nav.Item>
                      {docusignEnabled && (
                        <Nav.Item>
                          <Nav.Link eventKey="esign">{t('esign_audit')}</Nav.Link>
                        </Nav.Item>
                      )}
                    </Nav>
                    <Tab.Content>
                      <Tab.Pane eventKey="all">
                        <DocumentHistory documentId={documentId} active={activeTab === DocumentDetailNavIDs.History} />
                      </Tab.Pane>
                      <Tab.Pane eventKey="workflow">
                        {workflowHistoryLoading ? (
                          <Spinner animation="border" size="sm" />
                        ) : workflowHistory.length === 0 ? (
                          <p className="text-muted small mb-0">{t('document_workflow.history.empty')}</p>
                        ) : (
                          <Table size="sm" bordered className="mb-0">
                            <thead>
                              <tr>
                                <th>{t('date')}</th>
                                <th>{t('document_workflow.history.action')}</th>
                                <th>{t('document_workflow.history.performed_by')}</th>
                                <th>{t('document_workflow.workflow_name')}</th>
                                <th>{t('document_workflow.current_status')}</th>
                                <th>{t('document_workflow.next_status')}</th>
                                <th>{t('document_workflow.assign_view')}</th>
                                <th>{t('document_workflow.comment')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {workflowHistory.map((e, idx) => (
                                <tr key={idx}>
                                  <td className="small text-nowrap">{formatDateTime(e.performed_at)}</td>
                                  <td className="small">
                                    {e.entry_type === 'status_change'
                                      ? t('document_workflow.history.status_change')
                                      : e.entry_type === 'claim'
                                        ? t('document_workflow.history.claim')
                                        : t('document_workflow.history.unclaim')}
                                  </td>
                                  <td className="small">{e.performed_by_username ?? '—'}</td>
                                  <td className="small">{e.entry_type === 'status_change' ? (e.workflow_definition_name ?? '—') : '—'}</td>
                                  <td className="small">{e.from_status_name ?? '—'}</td>
                                  <td className="small">{e.to_status_name ?? '—'}</td>
                                  <td className="small">
                                    {(e.assigned_user_ids?.length || e.assigned_group_ids?.length) && e.entry_type === 'status_change'
                                      ? [
                                          e.assigned_user_ids?.length ? `${e.assigned_user_ids.length} user(s)` : null,
                                          e.assigned_group_ids?.length ? `${e.assigned_group_ids.length} group(s)` : null,
                                        ]
                                          .filter(Boolean)
                                          .join(', ')
                                      : '—'}
                                  </td>
                                  <td className="small">{e.comment || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </Tab.Pane>
                      {docusignEnabled && (
                        <Tab.Pane eventKey="esign">
                          <DocumentESignAudit documentId={documentId} />
                        </Tab.Pane>
                      )}
                    </Tab.Content>
                  </Tab.Container>
                </Tab.Pane>
              )}

              {/* PERMISSIONS TAB */}
              <Tab.Pane eventKey={DocumentDetailNavIDs.Permissions}>
                <div className="mb-3">
                  <PermissionsForm
                    value={permissionsForm}
                    onChange={handlePermissionsFormChange}
                    showTitle={false}
                    ownerDisplayFallback={document ? ownerDisplayFromDocument(document) : null}
                    permissionUsersFallback={permissionUsersFallback}
                    permissionGroupsFallback={permissionGroupsFallback}
                    disabled={!userCanEdit}
                  />
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Col>

        {/* Right column - Preview */}
        <Col md={6} xl={7}>
          <div className="preview-sticky">
            {previewLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
                <Spinner animation="border" />
              </div>
            ) : previewText ? (
              <div
                className="text-preview-container"
                style={{
                  height: '100%',
                  overflow: 'auto',
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {previewText}
                </pre>
              </div>
            ) : previewObjectUrl ? (
              isPdf ? (
                <div className="pdf-viewer-container">
                  <object
                    data={previewObjectUrl}
                    type="application/pdf"
                    style={{
                      width: '100%',
                      height: '100%',
                      transform: `scale(${pdfZoom})`,
                      transformOrigin: 'top center',
                    }}
                  >
                    <p>
                      {t('pdf_preview_not_supported')}
                      <a href={previewObjectUrl} download={document.title}>
                        {t('download_document')}
                      </a>
                    </p>
                  </object>
                </div>
              ) : (previewMimeType || document.mime_type).startsWith('image/') ? (
                <div className="preview-sticky">
                  <img
                    src={previewObjectUrl}
                    alt={document.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: 'top',
                    }}
                  />
                </div>
              ) : (
                <div className="text-center text-muted p-5">
                  {t('preview_not_available')}{' '}
                  <a href={previewObjectUrl} download={document.title}>
                    {t('download_document')}
                  </a>
                </div>
              )
            ) : (
              <div className="text-center text-muted p-5">{t('preview_not_available')}</div>
            )}
          </div>
        </Col>
      </Row>

      {/* Confirm Dialog */}
      <ConfirmDialog
        show={showConfirmDialog}
        title={confirmDialogConfig.title}
        message={confirmDialogConfig.message}
        messageBold={confirmDialogConfig.messageBold}
        confirmText={
          confirmDialogConfig.showPermanentDeleteOption
            ? undefined
            : isRefreshTemplateSubmitting || isDeleteSubmitting
              ? t('loading') || 'Učitavanje...'
              : confirmDialogConfig.confirmText
        }
        cancelText={t('cancel')}
        variant={confirmDialogConfig.variant}
        showPermanentDeleteOption={confirmDialogConfig.showPermanentDeleteOption}
        permanentDeleteLabel={confirmDialogConfig.permanentDeleteLabel}
        permanentDeleteDescription={confirmDialogConfig.permanentDeleteDescription}
        isConfirming={isRefreshTemplateSubmitting || isDeleteSubmitting}
        onConfirm={(permanentDelete?: boolean) => {
          if (confirmAction) {
            confirmAction(permanentDelete)
          }
        }}
        onCancel={() => {
          if (!isRefreshTemplateSubmitting && !isDeleteSubmitting) {
            setShowConfirmDialog(false)
          }
        }}
      />

      {/* Share Links Dialog */}
      {showShareLinksDialog && document && (
        <ShareLinksDialog
          show={showShareLinksDialog}
          onHide={() => setShowShareLinksDialog(false)}
          documentId={document.id}
          hasArchiveVersion={!!document.archived_file_name}
        />
      )}

      {/* Email Document Dialog */}
      {showEmailDialog && document && (
        <EmailDocumentDialog
          show={showEmailDialog}
          onHide={() => setShowEmailDialog(false)}
          documentIds={[document.id]}
          hasArchiveVersion={!!document.archived_file_name}
        />
      )}

      {/* DocuSign Send for Signature Dialog */}
      {showDocuSignSendDialog && document && (
        <DocuSignSendDialog show={showDocuSignSendDialog} onHide={() => setShowDocuSignSendDialog(false)} documentId={document.id} />
      )}
      {/* Workflow Dialog */}
      {/* PDF Editor Dialog */}
      {showPdfEditor && document && (
        <PDFEditor
          show={showPdfEditor}
          onHide={() => setShowPdfEditor(false)}
          documentId={document.id}
          onSuccess={(result) => {
            if (!document) return

            if (result?.deletedOriginal) {
              openDocumentsService.closeDocument(document)
              void documentListViewService.reload()
              if (documentListViewService.activeSavedViewId) {
                navigate(`/view/${documentListViewService.activeSavedViewId}`)
              } else {
                navigate('/documents')
              }
              return
            }

            loadDocument()
          }}
        />
      )}

      {/* Version Warning Dialog */}
      <ConfirmDialog
        show={showVersionWarning}
        title={t('document_changes_detected')}
        messageBold={t('document_version_older_than_existing')}
        message={t('document_save_may_overwrite_changes')}
        confirmText={t('ok')}
        onConfirm={() => {
          setShowVersionWarning(false)
          // User acknowledged the warning, they can continue with their changes
        }}
        onCancel={() => setShowVersionWarning(false)}
        variant="warning"
        hideCancelButton={true}
      />

      {showCloseDocumentDialog && document && (
        <ConfirmDialog
          show={showCloseDocumentDialog}
          title={t('unsaved_changes')}
          messageBold={`${t('you_have_unsaved_changes_to_the_document')} "${document.title}".`}
          message={t('are_you_sure_you_want_to_close_this_document')}
          confirmText={t('close_document')}
          onConfirm={handleConfirmCloseDocument}
          onCancel={handleCancelCloseDocument}
          variant="warning"
        />
      )}

      {showUnsavedWorkflowDialog && (
        <ConfirmDialog
          show={showUnsavedWorkflowDialog}
          title={t('unsaved_changes')}
          messageBold={t('document_workflow.unsaved_changes_before_action')}
          message={t('document_workflow.save_and_continue_prompt')}
          confirmText={t('document_workflow.save_and_continue')}
          cancelText={t('cancel')}
          variant="warning"
          isConfirming={workflowContinuing}
          onConfirm={handleConfirmSaveAndContinueWorkflow}
          onCancel={handleCancelSaveAndContinueWorkflow}
        />
      )}

      {/* Document Type Change Confirmation Dialog */}
      <ConfirmDialog
        show={showDocumentTypeChangeDialog}
        title={t('change_document_type') || 'Change Document Type'}
        messageBold={t('document_type_change_will_affect_custom_fields') || 'Changing document type will affect attributes.'}
        message={
          t('document_type_change_custom_fields_warning') ||
          'Attributes associated with the new document type will be automatically added to this document.'
        }
        confirmText={t('proceed') || 'Proceed'}
        cancelText={t('cancel')}
        onConfirm={handleDocumentTypeChangeConfirm}
        onCancel={handleDocumentTypeChangeCancel}
        variant="warning"
      >
        <div className="mt-3">
          <Form.Check
            type="radio"
            name="documentTypeCustomFieldsMode"
            id="documentTypeCustomFieldsModeKeep"
            checked={documentTypeChangeCustomFieldsMode === 'keep'}
            onChange={() => setDocumentTypeChangeCustomFieldsMode('keep')}
            label={t('document_type_change_keep_custom_fields')}
          />
          <Form.Check
            type="radio"
            name="documentTypeCustomFieldsMode"
            id="documentTypeCustomFieldsModeReplace"
            className="mt-2"
            checked={documentTypeChangeCustomFieldsMode === 'replace'}
            onChange={() => setDocumentTypeChangeCustomFieldsMode('replace')}
            label={t('document_type_change_replace_custom_fields')}
          />
        </div>
      </ConfirmDialog>

      {/* Create Correspondent Dialog */}
      {showCorrespondentDialog && (
        <CorrespondentEditDialog
          object={null}
          isCreating={true}
          onSuccess={handleCorrespondentCreated}
          onError={(error) => toastService.showError(t('error_creating_correspondent'), error)}
          onClose={() => setShowCorrespondentDialog(false)}
        />
      )}

      {/* Create Document Type Dialog */}
      {showDocumentTypeDialog && (
        <DocumentTypeEditDialog
          object={null}
          isCreating={true}
          onSuccess={handleDocumentTypeCreated}
          onError={(error) => toastService.showError(t('error_creating_document_type'), error)}
          onClose={() => setShowDocumentTypeDialog(false)}
        />
      )}

      {/* Create Workflow Status Dialog */}
      {showWorkflowStatusDialog && (
        <WorkflowStatusEditDialog
          object={null}
          isCreating={true}
          onSuccess={handleWorkflowStatusCreated}
          onError={(error: unknown) => toastService.showError(t('document_workflow.statuses.error_creating'), error)}
          onClose={() => setShowWorkflowStatusDialog(false)}
        />
      )}

      {/* Create Storage Path Dialog */}
      {showStoragePathDialog && (
        <StoragePathEditDialog
          object={undefined}
          isCreating={true}
          onSuccess={handleStoragePathCreated}
          onError={(error) => toastService.showError(t('error_creating_storage_path'), error)}
          onClose={() => setShowStoragePathDialog(false)}
        />
      )}

      {/* Create Tag Dialog */}
      {showTagDialog && (
        <TagEditDialog
          object={null}
          isCreating={true}
          onSuccess={handleTagCreated}
          onError={(error) => toastService.showError(t('error_creating_tag'), error)}
          onClose={() => setShowTagDialog(false)}
        />
      )}

      {/* Documentation Dialog for Actions */}
      <DocumentationDialog show={showDocsDialog} onHide={() => setShowDocsDialog(false)} docKey="document_detail_actions" />

      {/* Documentation Dialog for Tabs */}
      <DocumentationDialog show={showTabsDocsDialog} onHide={() => setShowTabsDocsDialog(false)} docKey="document_detail_tabs" />
    </div>
  )
}

export default DocumentDetail
